import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { prayerWallApi } from '../api/client';
import ReportModal from '../components/ReportModal';
import ShareToFeedModal from '../components/ShareToFeedModal';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  X, Pencil, Trash2, Check, ChevronLeft, ChevronRight,
  Globe, Flame, Users, CalendarDays, NotebookPen, AlertTriangle,
  HandHeart, Sprout, HelpCircle, Minus, FileText, Menu,
  Sparkles, RefreshCw, Copy, MessageCircle, Share, PartyPopper,
  ChevronDown, ChevronUp, Lock, MoveRight, RotateCcw,
  Tag, BookOpen, CheckCircle2, Clock, Flag,
  Compass, BookMarked, Heart, Plus, Languages,
  MoreHorizontal as MenuHamburger, Send, Eye,
  Shovel, Droplet, Leaf, TreeDeciduous, Wheat,
} from 'lucide-react';
import { PrayingHandsIcon, PriorityDot, CalDot } from '../components/Icons';
import { useTranslation } from '../i18n';
import { shortName } from '../utils/displayName';
import { sanitizeForDisplay } from '../utils/richText';
import './PrayerScreen.css';
import { useNavDots } from '../context/NavDotContext';
import './PrayerWallScreen.css'; // wall component styles (pw-* classes)

// Visibility → allowed feed visibilities (cannot share more openly than original)
const WALL_VIS_ALLOWED = {
  all:              ['public', 'connections', 'friends', 'groups'],
  'connections+groups': ['connections', 'friends', 'groups'],
  connections:      ['connections', 'friends', 'groups'],
  groups:           ['groups'],
};

// ── Multilingual prayer wall helpers ─────────────────────────────────────────
// Items and updates carry an opposite-language translation cache (titleAlt /
// descriptionAlt / answeredNoteAlt / contentAlt). These hooks pick the right
// rendering language based on the viewer's profile language and let the user
// toggle back to the original via a small badge.

function useLocalizedItem(item) {
  const { lang } = useTranslation();
  const [showOriginal, setShowOriginal] = useState(false);
  if (!item) return null;
  const sourceLang = item.sourceLang || 'en';
  const hasAlt = !!(item.titleAlt || item.descriptionAlt || item.answeredNoteAlt);
  const canTranslate = sourceLang !== lang && hasAlt;
  const useAlt = canTranslate && !showOriginal;
  return {
    title:        useAlt && item.titleAlt        ? item.titleAlt        : item.title,
    description:  useAlt && item.descriptionAlt  ? item.descriptionAlt  : item.description,
    answeredNote: useAlt && item.answeredNoteAlt ? item.answeredNoteAlt : item.answeredNote,
    isTranslated: useAlt,
    canTranslate,
    sourceLang,
    toggle: () => setShowOriginal(v => !v),
  };
}

function useLocalizedUpdate(update) {
  const { lang } = useTranslation();
  const [showOriginal, setShowOriginal] = useState(false);
  if (!update) return null;
  const sourceLang = update.sourceLang || 'en';
  const canTranslate = sourceLang !== lang && !!update.contentAlt;
  const useAlt = canTranslate && !showOriginal;
  return {
    content: useAlt && update.contentAlt ? update.contentAlt : update.content,
    isTranslated: useAlt,
    canTranslate,
    sourceLang,
    toggle: () => setShowOriginal(v => !v),
  };
}

function TranslationBadge({ isTranslated, canTranslate, sourceLang, onClick, compact = false }) {
  const { t } = useTranslation('prayerWall');
  if (!canTranslate) return null;
  const fromName = sourceLang === 'es' ? t('langSpanish') : t('langEnglish');
  return (
    <button type="button"
      className={`pw-translated-badge${compact ? ' pw-translated-badge-compact' : ''}`}
      onClick={(e) => { e.stopPropagation(); onClick(); }}>
      <Languages size={10}/>
      {isTranslated ? t('translatedFrom', fromName) : t('showTranslation')}
    </button>
  );
}

// Render one prayer-wall update with auto-translation badge
function LocalizedUpdateRow({ update, canDelete, onDelete, variant = 'rq' }) {
  const local = useLocalizedUpdate(update);
  const cls   = variant === 'cal' ? 'cal-update-item' : 'rq-update-item';
  const dateCls = variant === 'cal' ? 'cal-update-date' : 'rq-update-date';
  const textCls = variant === 'cal' ? 'cal-update-text' : 'rq-update-text';
  return (
    <div className={cls}>
      <span className={dateCls}>{fmtRelative(update.createdAt)}</span>
      {variant === 'cal'
        ? <p className={textCls}>{local.content}</p>
        : <span className={textCls}>{local.content}</span>}
      <TranslationBadge isTranslated={local.isTranslated} canTranslate={local.canTranslate}
        sourceLang={local.sourceLang} onClick={local.toggle} compact/>
      {canDelete && (
        <button className="rq-update-delete" onClick={onDelete} title="Delete">
          <X size={10}/>
        </button>
      )}
    </div>
  );
}

// ── Static (colors only — labels via t()) ────────────────────────────────────
// ── RichTextarea — contentEditable WYSIWYG with formatting toolbar ──────────
// Stores value as HTML (innerHTML). Displays formatted text live in the editor.

// Plain-text paste: escape it so a typed "<3" or "a < b" survives as characters
// rather than being parsed as a tag, and keep the line breaks.
function escapeAsHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r\n?/g, '\n')
    .replace(/\n/g, '<br>');
}

// Display sanitization lives in utils/richText.js now. The regex version that
// used to sit here allowed <span> through and stripped no attributes, which is
// how `style="background-color: rgba(0,0,0,0)"` out of a paste ended up rendered
// — and an `onmouseover` on that same span would have gone the same way. The
// shared helper is DOMParser-based and removes every attribute.

function RichTextarea({ value, onChange, placeholder, maxHeight = 320, className = '' }) {
  const ref       = useRef(null);
  const skipSync  = useRef(false); // prevent cursor-jump on onChange re-render

  // Sync external value → DOM only when not actively editing
  useEffect(() => {
    const el = ref.current;
    if (!el || skipSync.current) return;
    if (el.innerHTML !== (value || '')) el.innerHTML = value || '';
  }, [value]);

  function execFmt(cmd) {
    ref.current?.focus();
    document.execCommand(cmd, false, null);
    emitChange();
  }

  function emitChange() {
    skipSync.current = true;
    onChange(ref.current?.innerHTML || '');
    // Allow sync again after React has re-rendered
    requestAnimationFrame(() => { skipSync.current = false; });
  }

  function handleInput() { emitChange(); }

  /**
   * Paste clean, rather than cleaning up afterwards.
   *
   * The default paste drops whatever the source app put on the clipboard
   * straight into the DOM. A paste out of Google Docs or a browser carries
   * `<span style="background-color: rgba(0, 0, 0, 0); font-family: inherit">`
   * per run of text — that is the fingerprint on the value that started this,
   * and the editor showed it as normal text so there was no way to tell.
   *
   * The server sanitizes on save and the readers sanitize on render, so this is
   * not the safety net; it is so that what gets stored matches what was typed.
   */
  function handlePaste(e) {
    const cd = e.clipboardData;
    if (!cd) return;
    e.preventDefault();
    const html = cd.getData('text/html');
    const text = cd.getData('text/plain');
    // Prefer the HTML flavour so bold and lists survive a copy between two
    // fields in this app; fall back to text, escaped, with newlines kept.
    const clean = html
      ? sanitizeForDisplay(html)
      : escapeAsHtml(text);
    if (!clean) return;
    document.execCommand('insertHTML', false, clean);
    emitChange();
  }

  function handleKeyDown(e) {
    // Ctrl/Cmd + B/I/U shortcuts
    if (e.key === 'b' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); execFmt('bold'); }
    if (e.key === 'i' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); execFmt('italic'); }
    if (e.key === 'u' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); execFmt('underline'); }
  }

  // Constrain height via CSS, not JS (contentEditable grows naturally)
  const style = { maxHeight: maxHeight + 'px', overflowY: 'auto' };

  const isEmpty = !value || value === '<br>' || value === '';

  return (
    <div className="rte-wrap">
      <div className="rte-toolbar">
        <button type="button" className="rte-btn" title="Bold (Ctrl+B)"
          onMouseDown={e=>{e.preventDefault(); execFmt('bold');}}><strong>B</strong></button>
        <button type="button" className="rte-btn rte-italic" title="Italic (Ctrl+I)"
          onMouseDown={e=>{e.preventDefault(); execFmt('italic');}}><em>I</em></button>
        <button type="button" className="rte-btn rte-underline" title="Underline (Ctrl+U)"
          onMouseDown={e=>{e.preventDefault(); execFmt('underline');}}><u>U</u></button>
        <button type="button" className="rte-btn" title="Bulleted list"
          onMouseDown={e=>{e.preventDefault(); execFmt('insertUnorderedList');}}>•</button>
      </div>
      <div
        ref={ref}
        className={`rte-textarea ${className}`}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        style={style}
        aria-placeholder={placeholder}
      />
      {isEmpty && <span className="rte-placeholder" aria-hidden>{placeholder}</span>}
    </div>
  );
}

// Render stored HTML safely in display mode
function renderNotes(html) {
  const clean = sanitizeForDisplay(html);
  if (!clean) return null;
  return <span dangerouslySetInnerHTML={{ __html: clean }}/>;
}

// ── Notes editor modal — slide-up sheet (matches CreatePostModal on Feed) ────
function NotesEditorModal({ open, initialValue, title, subtitle, placeholder, saving, onSave, onClose }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initialValue || '');

  useEffect(() => {
    if (open) setDraft(initialValue || '');
  }, [open, initialValue]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal prayer-notes-modal" role="dialog" aria-modal="true">
        <div className="prayer-notes-modal-header">
          <div className="prayer-notes-modal-titlewrap">
            <span className="modal-title" style={{display:'inline-flex',alignItems:'center',gap:8}}>
              <NotebookPen size={18}/> {title}
            </span>
            {subtitle && <div className="prayer-notes-modal-subtitle">{subtitle}</div>}
          </div>
          <button className="modal-close" onClick={onClose} aria-label={t('common.close')}>
            <X size={16}/>
          </button>
        </div>
        <RichTextarea
          value={draft}
          onChange={setDraft}
          placeholder={placeholder}
          maxHeight={520}
        />
        <div className="prayer-notes-modal-actions">
          <button className="prayer-notes-cancel-btn" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button className="prayer-notes-save-btn" onClick={() => onSave(draft)} disabled={saving}>
            {saving ? '…' : <><Check size={15} strokeWidth={3}/> {t('common.save')}</>}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const PRIORITY_META = {
  normal: { color: '#2A6B6E', bg: '#EAF4F4' },
  high:   { color: '#C07800', bg: '#FDF6E3' },
  urgent: { color: '#C0392B', bg: '#FDF0F0' },
};

const WALL_OWN_COLOR    = '#0284C7';
const WALL_OWN_BG       = 'rgba(2,132,199,0.08)';
const WALL_OWN_GRADIENT = 'linear-gradient(135deg,#075985 0%,#0369a1 40%,#0284C7 100%)';
const WALL_NET_COLOR    = '#16A34A';
const WALL_NET_BG       = 'rgba(22,163,74,0.08)';
const WALL_NET_GRADIENT = 'linear-gradient(135deg,#14532d 0%,#15803d 40%,#16A34A 100%)';

// ── Prayer Wall constants ──────────────────────────────────────────────────────
const CATEGORIES_EN = [
  'Personal','Family','Health','Financial','Relationships',
  'Church','Nation','World / Disaster','Discernment',
  'Gratitude','Spiritual Growth','Work / Career','Other',
];
const CATEGORY_ICONS = {
  'Personal':'🙏','Family':'👨‍👩‍👧','Health':'❤️','Financial':'🌿',
  'Relationships':'💛','Church':'⛪','Nation':'🏛️','World / Disaster':'🌍',
  'Discernment':'🕊️','Gratitude':'✨','Spiritual Growth':'📖',
  'Work / Career':'💼','Other':'📌',
};
const VISIBILITY_OPTIONS = [
  { value:'private',     Icon:Lock,  labelKey:'visPrivate'     },
  { value:'connections', Icon:Users, labelKey:'visConnections' },
  { value:'groups',      Icon:Users, labelKey:'visGroups'      },
  { value:'all',         Icon:Globe, labelKey:'visAll'         },
];
function fmtDateWall(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
}

function fmtRelative(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Skip joiner words (and / & / y …) so "Rachel and Vince" → RV, "Clare & Shawn" → CS.
const INITIAL_STOPWORDS = new Set(['and', 'y', 'e', 'of', 'the', 'la', 'el']);
function getInitials(n = '') {
  const words = (n || '').split(/[\s&+/]+/).filter(w => w && !INITIAL_STOPWORDS.has(w.toLowerCase()));
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}
export function ymd(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }
export function ym(d)  { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d)   { return new Date(d.getFullYear(), d.getMonth()+1, 0); }
function gridStart(d) { const s=startOfMonth(d); return new Date(s.getFullYear(),s.getMonth(),s.getDate()-s.getDay()); }
function gridEnd(d)   { const e=endOfMonth(d);   return new Date(e.getFullYear(),e.getMonth(),e.getDate()+(6-e.getDay())); }
export function weekRange(d) {
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay());
  const end   = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  return { start, end };
}
export function getVisibleDays(viewDate, mode) {
  const days = [];
  let cur, end;
  if (mode === 'week') {
    const r = weekRange(viewDate); cur = new Date(r.start); end = r.end;
  } else {
    cur = new Date(gridStart(viewDate)); end = gridEnd(viewDate);
  }
  while (cur <= end) { days.push(new Date(cur)); cur.setDate(cur.getDate()+1); }
  return days;
}
export function visibleMonthsForRange(viewDate, mode) {
  const days = getVisibleDays(viewDate, mode);
  const set = new Set(days.map(d => ym(d)));
  return Array.from(set);
}

// Deterministic cover photo pick: same event always returns the same photo,
// different events spread across the pool. Returns null on empty pool so
// callers can fall back to the gradient.
function pickCoverPhoto(eventId, pool) {
  if (!eventId || !Array.isArray(pool) || pool.length === 0) return null;
  let sum = 0;
  for (let i = 0; i < eventId.length; i++) sum += eventId.charCodeAt(i);
  return pool[sum % pool.length];
}

// ── Event avatars ─────────────────────────────────────────────────────────────
// Deterministic gradient per name so each external recipient keeps a stable,
// distinct, professional-looking initials avatar across visits.
const AVATAR_PALETTE = [
  ['#6366F1', '#4F46E5'], // indigo
  ['#EC4899', '#DB2777'], // rose
  ['#14B8A6', '#0D9488'], // teal
  ['#F59E0B', '#D97706'], // amber
  ['#3B82F6', '#2563EB'], // blue
  ['#8B5CF6', '#7C3AED'], // violet
  ['#10B981', '#059669'], // emerald
  ['#0EA5E9', '#0284C7'], // sky
  ['#F97316', '#EA580C'], // orange
  ['#D946EF', '#C026D3'], // fuchsia
];
function avatarPalette(name = '') {
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum = (sum + name.charCodeAt(i)) % 9973;
  return AVATAR_PALETTE[sum % AVATAR_PALETTE.length];
}

// One circular avatar that also carries status: a priority-colored ring and a
// green check badge once prayed. Content priority: photo → memorial candle →
// category emoji → name-derived initials.
export function EventAvatar({ src, name, emoji, ringColor, prayed, memorial }) {
  let inner, bgStyle = {};
  if (src) {
    inner = <img src={src} alt="" className="cal-event-avatar-media"/>;
  } else if (memorial) {
    bgStyle = { background: 'linear-gradient(135deg, #6d28d9, #8b5cf6)' };
    inner = <span className="cal-event-avatar-glyph" aria-hidden>🕯️</span>;
  } else if (emoji) {
    bgStyle = { background: ringColor ? `${ringColor}22` : 'var(--gray-1)' };
    inner = <span className="cal-event-avatar-glyph" aria-hidden>{emoji}</span>;
  } else {
    const [c1, c2] = avatarPalette(name);
    bgStyle = { background: `linear-gradient(135deg, ${c1}, ${c2})` };
    inner = <span className="cal-event-avatar-initials">{getInitials(name)}</span>;
  }
  return (
    <div className="cal-event-avatar" style={{ '--ring': ringColor || 'var(--gray-2)' }}>
      <div className="cal-event-avatar-inner" style={bgStyle}>{inner}</div>
      {prayed && (
        <span className="cal-event-avatar-check" aria-hidden>
          <Check size={10} strokeWidth={3.5}/>
        </span>
      )}
    </div>
  );
}

// ── Prayer momentum hero card (above the calendar grid) ───────────────────────
// Circular progress ring — today's prayed / total, remaining count in the center.
function ProgressRing({ pct, allDone, value, size = 46, stroke = 4.5 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const clamped = Math.min(1, Math.max(0, pct));
  const offset = circ * (1 - (allDone ? 1 : clamped));
  const color = allDone ? '#16A34A' : 'var(--orange)';
  return (
    <div className="cal-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--gray-2)" strokeWidth={stroke}/>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset 0.45s ease' }}/>
      </svg>
      <div className="cal-ring-center">
        {allDone
          ? <Check size={19} strokeWidth={3} color="#16A34A"/>
          : <span className="cal-ring-num">{value}</span>}
      </div>
    </div>
  );
}

// Pick a single motivational line for the current streak state. Best streak is
// surfaced here (rather than as its own stat) to keep momentum front and center.
function getMotivation({ allDone, currentStreak, bestStreak, t }) {
  if (allDone) {
    return (currentStreak > 1 && currentStreak >= bestStreak)
      ? t('prayer.motivAllDoneStreak')
      : t('prayer.motivAllDone');
  }
  if (currentStreak === 0) return t('prayer.motivStart');
  if (bestStreak > currentStreak) return t('prayer.motivBeatBest', bestStreak - currentStreak, bestStreak);
  if (currentStreak === bestStreak && currentStreak > 1) return t('prayer.motivNewRecord');
  return t('prayer.motivGoingStrong', currentStreak);
}

export function PrayerStatsCard({ unprayedToday, totalToday = 0, currentStreak = 0, bestStreak = 0, onPrayNow, t }) {
  const allDone     = unprayedToday === 0;
  const prayedToday = Math.max(0, totalToday - unprayedToday);
  const pct         = totalToday > 0 ? prayedToday / totalToday : 0;
  const motivation  = getMotivation({ allDone, currentStreak, bestStreak, t });

  return (
    <div className="card cal-hero-card">
      <div className="cal-hero-main">
        <ProgressRing pct={pct} allDone={allDone} value={unprayedToday}/>
        <div className="cal-hero-text">
          <div className="cal-hero-headline">
            {allDone ? t('prayer.statsAllPrayed') : t('prayer.statsStillToday', unprayedToday)}
          </div>
          {motivation && <div className="cal-hero-sub">{motivation}</div>}
        </div>
        {!allDone && (
          <button className="cal-hero-praynow" onClick={onPrayNow}>
            {t('prayer.prayNowBtn')}
          </button>
        )}
      </div>
    </div>
  );
}

// ── User Search Picker ───────────────────────────────────────────────────────
function UserSearchPicker({ value, onChange }) {
  const { t } = useTranslation();
  const [query, setQuery]     = useState(value?.name||'');
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const ref = useRef();
  useEffect(() => {
    if (!query.trim() || query===value?.name) { setResults([]); return; }
    const timer = setTimeout(() => {
      api.get(`/api/users?q=${encodeURIComponent(query)}&limit=6`)
        .then(d => { setResults(d.users||[]); setOpen(true); }).catch(()=>{});
    }, 300);
    return () => clearTimeout(timer);
  }, [query, value]);
  useEffect(() => {
    const fn = e => { if(ref.current&&!ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn); return () => document.removeEventListener('mousedown', fn);
  }, []);
  return (
    <div className="user-search-wrap" ref={ref}>
      <input className="pr-input" placeholder={t('prayer.searchMembers')} value={query}
        onChange={e => { setQuery(e.target.value); if(!e.target.value) onChange(null); }}
        onFocus={() => results.length && setOpen(true)}/>
      {open && results.length > 0 && (
        <div className="user-search-dropdown">
          {results.map(u => (
            <button key={u.id} type="button" className="user-search-item"
              onClick={() => { onChange({id:u.id,name:u.name,username:u.username,avatar:u.avatar}); setQuery(u.name); setOpen(false); }}>
              {u.avatar ? <img src={u.avatar} alt="" className="usi-avatar-img"/> : <div className="usi-avatar">{getInitials(u.name)}</div>}
              <div><span className="usi-name">{u.name}</span>{u.username&&<span className="usi-username"> @{u.username}</span>}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Recipient Form Modal ─────────────────────────────────────────────────────
// The canonical relationship list. Values stored on the recipient are always
// these English strings; the label shown comes from prayer.relationships at the
// same index, so the order here is load-bearing — never reorder or insert.
//
// There used to be a second, byte-identical copy of this array named
// PRAYER_EN_RELATIONSHIPS, declared further down the file, plus a
// RELATIONSHIPS_EN alias pointing at it. Both were removed as collateral
// when RecipientCard was deleted, which left RecipientModal and
// QuickAddRecipientsModal throwing a ReferenceError on every render. One list,
// one name, so there is nothing left to lose track of.
const RELATIONSHIPS_EN = ['Husband','Wife','Fiancé','Mother','Step Mother','Father','Step Father','Brother','Step Brother','Sister','Step Sister','Son','Step Son','Daughter','Step Daughter','Cousin','Uncle','Aunt','Niece','Nephew','Grandson','Granddaughter','Grandmother','Grandfather','Mother-in-law','Father-in-law','Brother-in-law','Sister-in-law','Son-in-law','Daughter-in-law','Boyfriend','Girlfriend','Best Friend','Close Friend','Friend','Neighbor','Employee','Employer','Co-worker','Acquaintance','Stranger','Church Family','Classmate','Pastor','Mentor','Mentee'];

export function RecipientModal({ recipient, onClose, onSave, onStartDiscipling }) {
  const { t } = useTranslation();
  const isEdit = !!recipient;
  // If the recipient pre-dates this feature (or carries an old vocabulary
  // value), fall back to 'till' so the modal never opens with no chair selected.
  const initialStage = WITNESS_FLOW.includes(recipient?.witnessStage)
    ? recipient.witnessStage
    : 'till';
  const [form, setForm] = useState({
    name: recipient?.name||'', recipientType: recipient?.recipientType||'external',
    linkedUser: recipient?.linkedUser||null, priority: recipient?.priority||'normal',
    relationship: recipient?.relationship||'',
    prayerRequest: recipient?.prayerRequest||'',
    isMemorial:        recipient?.isMemorial        || false,
    passedOn:          recipient?.passedOn           ? new Date(recipient.passedOn).toISOString().slice(0,10) : '',
    memorialFrequency: recipient?.memorialFrequency  || 'anniversaries',
    faithLegacy:       recipient?.faithLegacy        || '',
    witnessStage:      initialStage,
  });
  const [outreachBusy, setOutreachBusy] = useState(false);
  function pickStage(stage) { setForm(f => ({ ...f, witnessStage: stage })); }
  async function handleDiscipleClick() {
    if (!onStartDiscipling || !recipient || outreachBusy) return;
    setOutreachBusy(true);
    try { await onStartDiscipling(recipient); } catch (err) { setError(err.message || t('common.somethingWrong')); }
    setOutreachBusy(false);
  }
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const PRIORITY_OPTIONS = [
    { value:'normal', label:t('prayer.priorities.normal'), desc:t('prayer.priorities.normalDesc') },
    { value:'high',   label:t('prayer.priorities.high'),   desc:t('prayer.priorities.highDesc')   },
    { value:'urgent', label:t('prayer.priorities.urgent'), desc:t('prayer.priorities.urgentDesc') },
  ];
  // Grouped relationships — value is always English canonical, label is translated
  const translatedRels = t('prayer.relationships');
  const relGroups = {
    [t('prayer.relationshipGroups.immediate')]: RELATIONSHIPS_EN.slice(0, 15)
      .map((en, i) => ({ value: en, label: translatedRels[i] || en })),
    [t('prayer.relationshipGroups.extended')]:  RELATIONSHIPS_EN.slice(15, 30)
      .map((en, i) => ({ value: en, label: translatedRels[15 + i] || en })),
    [t('prayer.relationshipGroups.friends')]:   RELATIONSHIPS_EN.slice(30)
      .map((en, i) => ({ value: en, label: translatedRels[30 + i] || en })),
  };

  function set(k,v) { setForm(f=>({...f,[k]:v})); }
  async function handleSave(e) {
    e.preventDefault();
    if(!form.name.trim()) { setError(t('prayer.nameRequired')); return; }
    if(form.recipientType==='ignite_user'&&!form.linkedUser) { setError(t('prayer.selectMember')); return; }
    setSaving(true); setError('');
    try {
      await onSave({ name:form.name.trim(), recipientType:form.recipientType,
        linkedUserId:form.recipientType==='ignite_user'?form.linkedUser?.id:null,
        priority:form.priority, relationship:form.relationship||null,
        prayerRequest:form.prayerRequest||null,
        isMemorial: form.isMemorial,
        passedOn:   form.isMemorial && form.passedOn ? form.passedOn : null,
        memorialFrequency: form.memorialFrequency || 'anniversaries',
        faithLegacy: form.faithLegacy || null,
        witnessStage: form.witnessStage,
      });
      onClose();
    } catch(err) { setError(err.message || t('prayer.failedSave')); }
    finally { setSaving(false); }
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal pr-modal">
        <div className="modal-header">
          <span className="modal-title">{isEdit ? t('prayer.editRecipient') : t('prayer.addRecipient')}</span>
          <button className="modal-close" onClick={onClose}><X size={16}/></button>
        </div>
        {error && <div className="form-error">{error}</div>}
        <form onSubmit={handleSave} className="pr-form">
          <div className="pr-field">
            <label className="pr-label">{t('prayer.formType')}</label>
            <div className="pr-type-btns">
              <button type="button" className={`pr-type-btn ${form.recipientType==='external'?'active':''}`} onClick={()=>set('recipientType','external')}><Globe size={14}/> External</button>
              <button type="button" className={`pr-type-btn ${form.recipientType==='ignite_user'?'active':''}`} onClick={()=>set('recipientType','ignite_user')}><Flame size={14}/> {t('prayer.formIgniteMember')}</button>
            </div>
          </div>
          {form.recipientType==='ignite_user' && (
            <div className="pr-field">
              <label className="pr-label">{t('prayer.formIgniteMember')}</label>
              <UserSearchPicker value={form.linkedUser} onChange={v=>{set('linkedUser',v);if(v)set('name',v.name);}}/>
            </div>
          )}
          <div className="pr-field"><label className="pr-label">{t('prayer.formName')}</label><input className="pr-input" value={form.name} onChange={e=>set('name',e.target.value)} placeholder={t('prayer.formNamePlaceholder')}/></div>
          <div className="pr-field">
            <label className="pr-label">{t('prayer.formPriority')}</label>
            <div className="pr-priority-list">
              {PRIORITY_OPTIONS.map(p=>(
                <button key={p.value} type="button" className={`pr-priority-btn ${form.priority===p.value?'active':''}`}
                  style={form.priority===p.value?{background:PRIORITY_META[p.value].bg,borderColor:PRIORITY_META[p.value].color,color:PRIORITY_META[p.value].color}:{}}
                  onClick={()=>set('priority',p.value)}>
                  <span style={{display:'inline-flex',alignItems:'center',gap:6}}><PriorityDot color={PRIORITY_META[p.value].color} size={9}/> {p.label}</span><span className="pr-priority-desc">{p.desc}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="pr-field">
            <label className="pr-label">{t('prayer.formRelationship')}</label>
            <select className="pr-input" value={form.relationship} onChange={e=>set('relationship',e.target.value)}>
              <option value="">{t('prayer.formSelectRelationship')}</option>
              {Object.entries(relGroups).map(([group,items])=>(
                <optgroup key={group} label={`── ${group} ──`}>{items.map(({value,label})=><option key={value} value={value}>{label}</option>)}</optgroup>
              ))}
            </select>
          </div>
          <div className="pr-field">
            <label className="pr-label">{t('prayer.formRequest')}</label>
            <RichTextarea value={form.prayerRequest || ''} onChange={v=>set('prayerRequest',v)}
              placeholder={t('prayer.formRequestPlaceholder')} className="pr-input"/>
          </div>
          {/* ── Where are they on the journey? (six phases, grouped by faith) ── */}
          {!form.isMemorial && (
            <div className="pr-field">
              <label className="pr-label">{t('prayer.witness.title')}</label>
              <div className="pr-witness-intro">{t('prayer.witness.intro')}</div>
              {[
                { key: 'preFaith',  label: t('prayer.witness.preFaithLabel'),  stages: WITNESS_FLOW.slice(0, 3), startIdx: 0 },
                { key: 'postFaith', label: t('prayer.witness.postFaithLabel'), stages: WITNESS_FLOW.slice(3),    startIdx: 3 },
              ].map(group => (
                <div key={group.key} className={`pr-stage-group pr-stage-group-${group.key}`}>
                  <div className="pr-stage-group-label">{group.label}</div>
                  <div className="pr-stage-grid">
                    {group.stages.map((s, i) => {
                      const Icon = STAGE_ICONS[s];
                      const active = form.witnessStage === s;
                      const num = group.startIdx + i + 1;
                      return (
                        <button key={s} type="button"
                          className={`pr-stage-card${active ? ' active' : ''}`}
                          onClick={() => pickStage(s)}>
                          <div className="pr-stage-card-head">
                            <span className="pr-stage-num">{num}</span>
                            <Icon size={15} color="currentColor" strokeWidth={2}/>
                            <span className="pr-stage-name">{t(`prayer.witness.${s}`)}</span>
                          </div>
                          <div className="pr-stage-hint">{t(`prayer.witness.${s}Hint`)}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {isEdit && onStartDiscipling && BELIEVER_STAGES_SET.has(form.witnessStage) && recipient?.linkedUserId && (
                <div className="pr-witness-actions">
                  <button type="button" className="pr-witness-action pr-witness-action-primary" disabled={outreachBusy} onClick={handleDiscipleClick}>
                    <HandHeart size={14}/> {t('prayer.witness.startDiscipling')}
                  </button>
                </div>
              )}
            </div>
          )}
          {/* ── Memorial toggle ── */}
          <div className="pr-field">
            <div className="pr-memorial-toggle" onClick={() => set('isMemorial', !form.isMemorial)}
              style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',borderRadius:10,cursor:'pointer',
                background:form.isMemorial?'rgba(139,92,246,0.08)':'var(--gray-1)',
                border:`1.5px solid ${form.isMemorial?'#8b5cf6':'var(--border)'}`,transition:'all 0.2s'}}>
              <span style={{fontSize:20}}>🕯️</span>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,fontSize:13,color:form.isMemorial?'#8b5cf6':'var(--text)'}}>{t('prayer.memorial.toggle')}</div>
                <div style={{fontSize:11,color:'var(--text-lt)',marginTop:1}}>{t('prayer.memorial.toggleSub')}</div>
              </div>
              <div style={{width:36,height:20,borderRadius:10,background:form.isMemorial?'#8b5cf6':'var(--border)',
                transition:'background 0.2s',position:'relative',flexShrink:0}}>
                <div style={{position:'absolute',top:2,left:form.isMemorial?18:2,width:16,height:16,borderRadius:'50%',
                  background:'white',transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.2)'}}/>
              </div>
            </div>
          </div>

          {form.isMemorial && (
            <>
              <div className="pr-field">
                <label className="pr-label">
                  {t('prayer.memorial.passedOnLabel')} <span style={{fontWeight:400,color:'var(--text-lt)',fontSize:11}}>{t('prayer.memorial.passedOnOptional')}</span>
                </label>
                <input type="date" className="pr-input" value={form.passedOn}
                  onChange={e => set('passedOn', e.target.value)}
                  max={new Date().toISOString().slice(0,10)}/>
              </div>

              <div className="pr-field">
                <label className="pr-label">{t('prayer.memorial.frequencyLabel')}</label>
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {[
                    { value:'anniversaries', labelKey:'anniversaries', descKey:'anniversariesDesc' },
                    { value:'monthly',       labelKey:'monthly',       descKey:'monthlyDesc'       },
                    { value:'quarterly',     labelKey:'quarterly',     descKey:'quarterlyDesc'     },
                  ].map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => set('memorialFrequency', opt.value)}
                      style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 12px',borderRadius:8,
                        border:`1.5px solid ${form.memorialFrequency===opt.value?'#8b5cf6':'var(--border)'}`,
                        background:form.memorialFrequency===opt.value?'rgba(139,92,246,0.06)':'var(--surface)',
                        cursor:'pointer',textAlign:'left'}}>
                      <div style={{width:16,height:16,borderRadius:'50%',border:`2px solid ${form.memorialFrequency===opt.value?'#8b5cf6':'var(--border)'}`,
                        background:form.memorialFrequency===opt.value?'#8b5cf6':'transparent',flexShrink:0,marginTop:1,
                        display:'flex',alignItems:'center',justifyContent:'center'}}>
                        {form.memorialFrequency===opt.value && <div style={{width:6,height:6,borderRadius:'50%',background:'white'}}/>}
                      </div>
                      <div>
                        <div style={{fontWeight:600,fontSize:13,color:'var(--text)'}}>{t('prayer.memorial.frequencyOptions.'+opt.value)}</div>
                        <div style={{fontSize:11,color:'var(--text-lt)',marginTop:1}}>{t('prayer.memorial.frequencyOptions.'+opt.descKey)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="pr-field">
                <label className="pr-label">
                  {t('prayer.memorial.faithLegacyLabel')} <span style={{fontWeight:400,color:'var(--text-lt)',fontSize:11}}>{t('prayer.memorial.faithLegacyOptional')}</span>
                </label>
                <textarea className="pr-input" rows={2} value={form.faithLegacy}
                  onChange={e => set('faithLegacy', e.target.value)}
                  placeholder={t('prayer.memorial.faithLegacyPlaceholder')}
                  style={{resize:'none',fontFamily:'inherit'}}/>
              </div>
            </>
          )}

          <button className="btn btn-primary" type="submit" style={{width:'100%',marginTop:4}} disabled={saving}>
            {saving ? <span className="spinner"/> : isEdit ? t('common.save') : t('prayer.addRecipient')}
          </button>
        </form>
      </div>
    </div>, document.body
  );
}

// ── Invite share modal ───────────────────────────────────────────────────────
// Lets the user edit the personalized invite message before opening the native
// share sheet. Default text is stage-aware (per-chair template) with the
// recipient's first name interpolated.
export function InviteShareModal({ recipient, url, defaultText, onShare, onClose }) {
  const { t } = useTranslation();
  const [text, setText] = useState(defaultText || '');
  const [busy, setBusy] = useState(false);

  async function handleShare() {
    if (!text.trim() || busy) return;
    setBusy(true);
    try { await onShare(text.trim()); } catch {}
    setBusy(false);
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal pr-modal">
        <div className="modal-header">
          <span className="modal-title">{t('prayer.witness.shareModalTitle')}</span>
          <button className="modal-close" onClick={onClose}><X size={16}/></button>
        </div>
        <div style={{padding:'0 16px 16px'}}>
          <div className="pr-witness-intro" style={{margin:'-4px 0 12px'}}>
            {t('prayer.witness.shareModalIntro', firstName(recipient?.name) || recipient?.name)}
          </div>
          <textarea
            className="pr-input"
            value={text}
            onChange={e => setText(e.target.value)}
            rows={6}
            style={{resize:'vertical', fontFamily:'inherit', lineHeight:1.5}}/>
          <div style={{marginTop:8, fontSize:11, color:'var(--text-lt)'}}>
            <div style={{fontWeight:600, marginBottom:2}}>{t('prayer.witness.shareModalUrlLabel')}</div>
            <div style={{wordBreak:'break-all'}}>{url}</div>
          </div>
          <div style={{display:'flex', gap:10, marginTop:16}}>
            <button type="button" className="btn" onClick={onClose} disabled={busy} style={{flex:1}}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn-primary" onClick={handleShare} disabled={busy || !text.trim()}
              style={{flex:2, display:'inline-flex', alignItems:'center', justifyContent:'center', gap:7}}>
              <Send size={14}/> {t('prayer.witness.shareNow')}
            </button>
          </div>
        </div>
      </div>
    </div>, document.body
  );
}

// All sample prayers ever generated for an event (oldest → newest). Falls back
// to the single legacy `samplePrayer` field for events created before history
// tracking existed.
function samplePrayerList(ev) {
  if (Array.isArray(ev?.samplePrayerHistory) && ev.samplePrayerHistory.length) return ev.samplePrayerHistory;
  return ev?.samplePrayer ? [ev.samplePrayer] : [];
}

// ── Event Detail Panel ───────────────────────────────────────────────────────
export function EventDetail({ event, onClose, onPrayed, onNotesChange, onEventUpdate, coverPhotos, onInvite }) {
  const { t } = useTranslation();
  const r          = event.recipient;
  const isMemorial = r?.isMemorial || false;
  const meta       = isMemorial
    ? { color:'#8b5cf6', bg:'rgba(139,92,246,0.08)' }
    : (PRIORITY_META[r?.priority] || PRIORITY_META.normal);
  const priorityLabel = isMemorial
    ? t('prayer.memorial.recipientTag')
    : t(`prayer.priorities.${r?.priority || 'normal'}`);
  const MONTHS = t('prayer.months');
  const date = new Date(event.scheduledFor.slice(0, 10) + 'T00:00:00');
  const [notes, setNotes]             = useState(event.notes ?? r?.prayerRequest ?? '');
  const [editing, setEditing]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);
  const [praying, setPraying]         = useState(false);
  const [genPraying, setGenPraying]   = useState(false);
  const [showAIPrayer, setShowAIPrayer] = useState(!!event.samplePrayer);
  const [copied, setCopied]           = useState(false);
  const [inviting, setInviting]       = useState(false);
  const prayerHistory = samplePrayerList(event);
  const [aiIndex, setAiIndex]         = useState(Math.max(0, prayerHistory.length - 1));
  const safeIndex   = Math.min(aiIndex, Math.max(0, prayerHistory.length - 1));
  const currentPrayer = prayerHistory[safeIndex] || '';
  async function handleInviteClick() {
    if (inviting || !onInvite || !r) return;
    setInviting(true);
    try { await onInvite(r); }
    catch (err) { alert(err.message || t('common.somethingWrong')); }
    setInviting(false);
  }

  useEffect(() => {
    setNotes(event.notes ?? r?.prayerRequest ?? '');
    setEditing(false); setSaved(false);
    setShowAIPrayer(!!event.samplePrayer);
    // Snap to the newest prayer whenever this event's prayers change.
    setAiIndex(Math.max(0, samplePrayerList(event).length - 1));
  }, [event.id, event.notes, event.samplePrayer, r?.prayerRequest]);

  async function saveNotes(nextNotes) {
    const value = typeof nextNotes === 'string' ? nextNotes : notes;
    setSaving(true);
    try {
      await api.patch(`/api/prayer/calendar/${event.id}`, { notes: value });
      onNotesChange(event.id, value);
      setNotes(value);
      setSaved(true); setEditing(false);
      setTimeout(() => setSaved(false), 2000);
    } catch(err) { alert(err.message || t('prayer.couldNotSaveNotes')); }
    finally { setSaving(false); }
  }

  async function handlePray() {
    if (event.prayedAt || praying) return;
    setPraying(true);
    try {
      const d = await api.post(`/api/prayer/calendar/${event.id}/prayed`);
      onPrayed(d.event);
    } catch(err) { alert(err.message || t('prayer.couldNotMarkPrayed')); }
    finally { setPraying(false); }
  }

  async function handleGeneratePrayer() {
    setGenPraying(true);
    try {
      const d = await api.post(`/api/prayer/calendar/${event.id}/generate-prayer`);
      onEventUpdate(d.event); setShowAIPrayer(true);
    } catch(err) { alert(err.message || t('prayer.couldNotGenerate')); }
    finally { setGenPraying(false); }
  }

  async function handleCopyPrayer() {
    try {
      await navigator.clipboard.writeText(currentPrayer);
      setCopied(true); setTimeout(() => setCopied(false), 2200);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = currentPrayer;
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      setCopied(true); setTimeout(() => setCopied(false), 2200);
    }
  }

  if (!event?.scheduledFor) return null;

  const COVER_GRADIENTS = {
    urgent: 'linear-gradient(135deg, #7f1d1d 0%, #9b1c1c 40%, #c0392b 100%)',
    high:   'linear-gradient(135deg, #78350f 0%, #92400e 40%, #c07800 100%)',
    normal: 'linear-gradient(135deg, #134e4a 0%, #0f766e 45%, #2A6B6E 100%)',
  };

  const pool  = isMemorial ? coverPhotos?.memorial : coverPhotos?.general;
  const cover = pickCoverPhoto(event.id, pool);
  const fallbackBg = isMemorial
    ? 'linear-gradient(135deg, #3b0764 0%, #6d28d9 50%, #8b5cf6 100%)'
    : (COVER_GRADIENTS[r?.priority] || COVER_GRADIENTS.normal);
  const coverStyle = cover
    ? { backgroundImage: `url(${cover.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: fallbackBg };

  return (
    <div className="cal-detail-panel card fade-up">
      <div className={`cal-detail-cover ${cover ? 'has-image' : ''}`} style={coverStyle}>
        {!cover && (
          <svg className="cal-cover-pattern" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="crossPat" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <rect x="17" y="8"  width="6" height="24" rx="1.5" fill="white" opacity="0.12"/>
                <rect x="10" y="14" width="20" height="6"  rx="1.5" fill="white" opacity="0.12"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#crossPat)"/>
          </svg>
        )}
        <div className="cal-cover-overlay"/>
        {isMemorial && <Flame className="cal-cover-flame" size={18} strokeWidth={2}/>}
        <button className="cal-detail-close" onClick={onClose} aria-label={t('common.close')}><X size={14}/></button>
        <div className="cal-date-badge">
          <span className="cal-date-m">{MONTHS[date.getMonth()].slice(0,3).toUpperCase()}</span>
          <span className="cal-date-d">{date.getDate()}</span>
        </div>
        <div className="cal-cover-text">
          <div className="cal-cover-kicker">
            {isMemorial
              ? <><Flame size={11} strokeWidth={2.5} style={{verticalAlign:'-1px',marginRight:3}}/> {t('prayer.memorial.calEventLabel')}</>
              : t('prayer.prayFor')}{' '}
            <span className="cal-cover-name-inline">{r?.name}</span>
          </div>
          {isMemorial
            ? <div className="cal-cover-rel" style={{opacity:0.85}}>{t('prayer.memorial.detailSub')}</div>
            : r?.relationship && <div className="cal-cover-rel">{(() => {
                const idx = RELATIONSHIPS_EN.indexOf(r.relationship);
                const rels = t('prayer.relationships');
                const localized = (Array.isArray(rels) && idx >= 0) ? rels[idx] : r.relationship;
                return t('prayer.myRelationship', localized);
              })()}</div>
          }
          <div className="cal-cover-meta">
            {isMemorial
              ? <span className="cal-meta-chip" style={{display:"inline-flex",alignItems:"center",gap:5}}><Flame size={11} strokeWidth={2.5}/> {t('prayer.memorial.recipientTag')}</span>
              : <span className="cal-meta-chip" style={{display:"inline-flex",alignItems:"center",gap:5}}><PriorityDot color="currentColor" size={8}/> {priorityLabel}</span>}
            {!isMemorial && (() => {
              const stage = WITNESS_FLOW.includes(r?.witnessStage) ? r.witnessStage : 'till';
              const Icon = STAGE_ICONS[stage];
              return (
                <span className={`cal-meta-chip cal-meta-journey journey-${stage}`} style={{display:'inline-flex',alignItems:'center',gap:5}}>
                  <Icon size={11} strokeWidth={2}/>
                  {t(`prayer.witness.${stage}`)}
                </span>
              );
            })()}
            {event.prayedAt && <span className="cal-meta-chip" style={{display:'inline-flex',alignItems:'center',gap:4}}><Check size={11} strokeWidth={3}/> {t('prayer.prayedToday')}</span>}
          </div>
        </div>
      </div>

      <div className="cal-detail-body">

        {isMemorial && r?.faithLegacy && (
          <div className="cal-legacy-banner" style={{margin:'12px 0 4px',padding:'10px 14px',borderRadius:8,background:'rgba(139,92,246,0.07)',border:'1px solid rgba(139,92,246,0.15)',fontSize:13,color:'var(--text)',lineHeight:1.5}}>
            <span style={{fontWeight:600,color:'#8b5cf6',marginRight:6}}>✦</span>{r.faithLegacy}
          </div>
        )}
        <div className="cal-notes-section">
          <div className="cal-notes-header">
            <span className="cal-notes-label" style={{display:"inline-flex",alignItems:"center",gap:5}}><NotebookPen size={13}/> {t('prayer.myNotes')}</span>
            {!editing && (
              <button className="cal-notes-edit-btn" onClick={() => setEditing(true)}>
                {notes ? t('prayer.editNotes') : t('prayer.addNotes')}
              </button>
            )}
          </div>
          {notes ? (
            <div className="cal-notes-display" onClick={() => setEditing(true)}>
              <p>{renderNotes(notes)}</p>
              {saved && <span className="cal-notes-saved" style={{display:'inline-flex',alignItems:'center',gap:3}}><Check size={11} strokeWidth={3}/> Saved</span>}
            </div>
          ) : (
            <button className="cal-notes-empty" onClick={() => setEditing(true)}>
              {t('prayer.addNotes')}
            </button>
          )}
        </div>
        <NotesEditorModal
          open={editing}
          initialValue={notes}
          title={t('prayer.editingMyNotes')}
          subtitle={r?.name ? `${t('prayer.prayFor')} ${r.name}` : ''}
          placeholder={t('prayer.formRequestPlaceholder')}
          saving={saving}
          onSave={saveNotes}
          onClose={() => setEditing(false)}
        />

        <div className="cal-ai-section">
          {showAIPrayer && currentPrayer ? (
            <div className="cal-ai-prayer-block">
              <div className="cal-ai-header">
                <span className="cal-ai-label"><Sparkles size={12}/> {t('prayer.samplePrayer')}</span>
                {prayerHistory.length > 1 && (
                  <div className="cal-ai-nav">
                    <button className="cal-ai-nav-btn" onClick={() => setAiIndex(i => Math.max(0, i - 1))}
                      disabled={safeIndex === 0} aria-label={t('prayer.previousPrayer')}>
                      <ChevronLeft size={15}/>
                    </button>
                    <span className="cal-ai-nav-count">{safeIndex + 1} / {prayerHistory.length}</span>
                    <button className="cal-ai-nav-btn" onClick={() => setAiIndex(i => Math.min(prayerHistory.length - 1, i + 1))}
                      disabled={safeIndex >= prayerHistory.length - 1} aria-label={t('prayer.nextPrayer')}>
                      <ChevronRight size={15}/>
                    </button>
                  </div>
                )}
              </div>
              <p className="cal-ai-text">{currentPrayer}</p>
              <div className="cal-ai-actions-bar">
                <button className="cal-ai-action-btn" onClick={handleCopyPrayer}>
                  {copied ? <><Check size={14} strokeWidth={3}/> {t('prayNow.copied')}</> : <><Copy size={14}/> {t('prayer.copyPrayer')}</>}
                </button>
                <button className="cal-ai-action-btn" onClick={async () => {
                  if (navigator.share) {
                    try { await navigator.share({ title: 'Prayer for ' + (r?.name||''), text: currentPrayer }); } catch {}
                  } else { handleCopyPrayer(); }
                }}>
                  <Share size={14}/> {t('prayer.sharePrayer')}
                </button>
                <button className="cal-ai-action-btn cal-ai-action-regen" onClick={handleGeneratePrayer} disabled={genPraying}>
                  <RefreshCw size={14}/> {genPraying ? '…' : t('prayer.newPrayer')}
                </button>
              </div>
            </div>
          ) : (
            <button className="cal-ai-gen-btn" onClick={handleGeneratePrayer} disabled={genPraying}>
              {genPraying
                ? <><Sparkles size={13}/> {t('prayer.generatingPrayer')}</>
                : <><Sparkles size={13}/> {t('prayer.generatePrayer')}</>}
            </button>
          )}
        </div>

        {!isMemorial && (() => {
          // Stage-aware next-step prompt — replaces the generic
          // "Want to brighten their day?" nudge with a discipleship
          // action calibrated to where this person is on the journey.
          const stage = WITNESS_FLOW.includes(r?.witnessStage) ? r.witnessStage : 'till';
          const steps = t(`prayer.witness.nextSteps.${stage}`);
          if (!Array.isArray(steps) || !steps.length) return null;
          const text = steps[event.id ? event.id.charCodeAt(0) % steps.length : 0];
          const Icon = STAGE_ICONS[stage];
          return (
            <div className="cal-nudge cal-next-step">
              <Icon size={13} className="cal-nudge-icon"/>
              <div>
                <span className="cal-nudge-heading">{t('prayer.witness.nextStepsTitle')}</span>
                <span className="cal-nudge-msg">{text}</span>
              </div>
            </div>
          );
        })()}

        <button
          className={`cal-pray-btn ${event.prayedAt?'prayed':''} ${praying?'praying':''}`}
          onClick={handlePray} disabled={!!event.prayedAt || praying}
          style={event.prayedAt
            ? {background:meta.bg,color:meta.color,cursor:'default'}
            : {background: isMemorial ? '#8b5cf6' : meta.color}}>
          {event.prayedAt
            ? <><Check size={16} strokeWidth={3} color='currentColor'/> {t('prayer.prayedButton')}</>
            : praying
              ? <><PrayingHandsIcon size={16} color='currentColor'/> {t('prayer.prayingButton')}</>
              : isMemorial
                ? <><span style={{marginRight:4}}>🕯️</span>{t('prayer.memorial.prayedBtn')}</>
                : <><PrayingHandsIcon size={16} color='currentColor'/> {t('prayer.prayButton')}</>}
        </button>
        {!isMemorial && onInvite && r?.id && (
          <button className="cal-invite-btn" onClick={handleInviteClick} disabled={inviting}>
            <Send size={14} strokeWidth={2}/> {t('prayer.witness.inviteToIgnite')}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Calendar Grid ────────────────────────────────────────────────────────────
export function CalendarGrid({ events, viewDate, selectedDay, onSelectDay, currentUserId, mode = 'month' }) {
  const { t } = useTranslation();
  const WEEKDAYS = t('prayer.days');
  const byDay = {};
  events.forEach(e => {
    const key = e.scheduledFor.slice(0, 10);
    if (!byDay[key]) byDay[key] = [];
    byDay[key].push(e);
  });
  const todayKey = ymd(new Date());
  const days = getVisibleDays(viewDate, mode);

  return (
    <div className={`cal-grid-wrap ${mode === 'week' ? 'week' : ''}`}>
      <div className="cal-weekdays">{WEEKDAYS.map(w=><div key={w}>{w}</div>)}</div>
      <div className={`cal-grid ${mode === 'week' ? 'week' : ''}`}>
        {days.map(day => {
          const key = ymd(day);
          const inMonth = mode === 'week' ? true : day.getMonth() === viewDate.getMonth();
          const isToday = key === todayKey;
          const isSelected = key === selectedDay;
          const dayEvents = byDay[key] || [];
          return (
            <button key={key}
              className={`cal-day ${inMonth?'':'out'} ${isToday?'today':''} ${isSelected?'selected':''} ${dayEvents.length?'has-events':''}`}
              onClick={() => onSelectDay(key)}>
              <span className="cal-day-num">{day.getDate()}</span>
              {dayEvents.length > 0 && (
                <div className="cal-dots">
                  {dayEvents.slice(0,3).map((e,i) => {
                    const isWall = e._isWall || !!e.wallItem;
                    let dotColor;
                    if (isWall) {
                      const isOwn = e.wallItem?.userId === currentUserId;
                      dotColor = isOwn ? WALL_OWN_COLOR : WALL_NET_COLOR;
                    } else {
                      const meta = PRIORITY_META[e.recipient?.priority] || PRIORITY_META.normal;
                      dotColor = meta.color;
                    }
                    return <CalDot key={i} color={dotColor} prayed={!!e.prayedAt}/>;
                  })}
                  {dayEvents.length > 3 && <span className="cal-dot more"/>}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Day Event List ───────────────────────────────────────────────────────────

// ── Wall Event Detail ─────────────────────────────────────────────────────────
export function WallEventDetail({ event, onClose, onPrayed, onEventUpdate, currentUserId, coverPhotos }) {
  const { t } = useTranslation();
  const wi      = event.wallItem;
  const isOwn   = wi?.userId === currentUserId;
  const local   = useLocalizedItem(wi);
  const MONTHS  = t('prayer.months');
  const date    = new Date((event.scheduledFor||'').slice(0, 10) + 'T00:00:00');

  const [notes,        setNotes]        = useState(event.notes || (isOwn ? wi?.description : '') || '');
  const [editing,      setEditing]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);
  const [praying,      setPraying]      = useState(false);
  const [genPraying,   setGenPraying]   = useState(false);
  const [showAIPrayer, setShowAIPrayer] = useState(!!event.samplePrayer);
  const [copied,       setCopied]       = useState(false);
  const [localEvent,   setLocalEvent]   = useState(event);
  const [aiIndex,      setAiIndex]      = useState(Math.max(0, samplePrayerList(event).length - 1));
  const prayerHistory  = samplePrayerList(localEvent);
  const safeIndex      = Math.min(aiIndex, Math.max(0, prayerHistory.length - 1));
  const currentPrayer  = prayerHistory[safeIndex] || '';

  useEffect(() => {
    setNotes(event.notes || (isOwn ? wi?.description : '') || '');
    setEditing(false); setSaved(false);
    setShowAIPrayer(!!event.samplePrayer);
    setLocalEvent(event);
    // Snap to the newest prayer whenever a different event is opened.
    setAiIndex(Math.max(0, samplePrayerList(event).length - 1));
  }, [event.id, event.notes, event.samplePrayer]);

  const CATEGORY_ICONS = {
    'Personal':'🙏','Family':'👨‍👩‍👧','Health':'❤️','Financial':'🌿',
    'Relationships':'💛','Church':'⛪','Nation':'🏛️','World / Disaster':'🌍',
    'Discernment':'🕊️','Gratitude':'✨','Spiritual Growth':'📖',
    'Work / Career':'💼','Other':'📌',
  };
  const icon = CATEGORY_ICONS[wi?.category] || '📌';

  async function handlePray() {
    if (localEvent.prayedAt || praying) return;
    setPraying(true);
    try {
      const d = await api.post(`/api/prayer-wall/calendar/${localEvent.id}/prayed`);
      const updated = { ...localEvent, ...(d.event || {}), prayedAt: d.event?.prayedAt || new Date().toISOString() };
      setLocalEvent(updated);
      if (onPrayed) onPrayed(updated);
      // Auto-close after brief moment so user sees prayed state
      setTimeout(() => {
        onClose();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 1200);
    } catch(err) { alert(err.message || t('prayer.couldNotMarkPrayed')); }
    finally { setPraying(false); }
  }

  async function saveNotes(nextNotes) {
    const value = typeof nextNotes === 'string' ? nextNotes : notes;
    setSaving(true);
    try {
      await api.patch(`/api/prayer-wall/calendar/${localEvent.id}`, { notes: value });
      if (isOwn && wi?.id) {
        await api.put(`/api/prayer-wall/${wi.id}`, { description: value });
      }
      setNotes(value);
      setSaved(true); setEditing(false);
      setTimeout(() => setSaved(false), 2000);
    } catch(err) { alert(err.message || t('prayer.couldNotSaveNotes')); }
    finally { setSaving(false); }
  }

  async function handleGeneratePrayer() {
    setGenPraying(true);
    try {
      const d = await api.post(`/api/prayer-wall/calendar/${localEvent.id}/generate-prayer`);
      const updated = { ...localEvent, ...(d.event || {}) };
      setLocalEvent(updated);
      if (onEventUpdate) onEventUpdate(updated);
      setShowAIPrayer(true);
      // Snap to the freshly generated prayer (now the newest in history).
      setAiIndex(Math.max(0, samplePrayerList(updated).length - 1));
    } catch(err) { alert(err.message || t('prayer.couldNotGenerate')); }
    finally { setGenPraying(false); }
  }

  async function handleCopyPrayer() {
    try {
      await navigator.clipboard.writeText(currentPrayer);
      setCopied(true); setTimeout(() => setCopied(false), 2200);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = currentPrayer;
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      setCopied(true); setTimeout(() => setCopied(false), 2200);
    }
  }

  if (!event?.scheduledFor) return null;

  const WALL_GRADIENT = isOwn ? WALL_OWN_GRADIENT : WALL_NET_GRADIENT;
  const wallColor     = isOwn ? WALL_OWN_COLOR    : WALL_NET_COLOR;
  const wallBg        = isOwn ? WALL_OWN_BG       : WALL_NET_BG;
  const sublabel      = isOwn ? t('prayer.yourPrayerRequest') : t('prayer.fromNetwork', wi?.user?.name);

  // Wall events are never memorial — always pull from the general pool.
  const cover = pickCoverPhoto(event.id, coverPhotos?.general);
  const coverStyle = cover
    ? { backgroundImage: `url(${cover.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: WALL_GRADIENT };

  return (
    <div className="cal-detail-panel card fade-up">
      {/* Cover — same structure as EventDetail */}
      <div className={`cal-detail-cover ${cover ? 'has-image' : ''}`} style={coverStyle}>
        {!cover && (
          <svg className="cal-cover-pattern" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="crossPatW" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <rect x="17" y="8"  width="6" height="24" rx="1.5" fill="white" opacity="0.12"/>
                <rect x="10" y="14" width="20" height="6"  rx="1.5" fill="white" opacity="0.12"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#crossPatW)"/>
          </svg>
        )}
        <div className="cal-cover-overlay"/>
        <button className="cal-detail-close" onClick={onClose} aria-label={t('common.close')}><X size={14}/></button>
        <div className="cal-date-badge">
          <span className="cal-date-m">{MONTHS[date.getMonth()]?.slice(0,3).toUpperCase()}</span>
          <span className="cal-date-d">{date.getDate()}</span>
        </div>
        <div className="cal-cover-text">
          <div className="cal-cover-kicker">
            <span style={{fontSize:'1.1em'}}>{icon}</span> {local?.title || wi?.title}
          </div>
          <div className="cal-cover-rel">{sublabel}</div>
          <div className="cal-cover-meta">
            <span className="cal-meta-chip" style={{display:'inline-flex',alignItems:'center',gap:5}}>
              {icon} {wi?.category || 'Prayer Request'}
            </span>
            {localEvent.prayedAt && (
              <span className="cal-meta-chip" style={{display:'inline-flex',alignItems:'center',gap:4}}>
                <Check size={11} strokeWidth={3}/> {t('prayer.prayedToday')}
              </span>
            )}
            {/* Prayer counts */}
            {isOwn && localEvent.totalPrayerCount > 0 && (
              <span className="cal-meta-chip">
                🙏 {localEvent.totalPrayerCount} {localEvent.totalPrayerCount === 1 ? 'prayer' : 'prayers'}
              </span>
            )}
            {!isOwn && localEvent.myPrayerCount > 0 && (
              <span className="cal-meta-chip" style={{fontWeight:700}}>
                {t('prayer.youPrayed', localEvent.myPrayerCount)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="cal-detail-body">

        {/* Notes section */}
        <div className="cal-notes-section">
          <div className="cal-notes-header">
            <span className="cal-notes-label" style={{display:'inline-flex',alignItems:'center',gap:5}}>
              <NotebookPen size={13}/> {isOwn ? t('prayer.myNotes') : t('prayer.prayerRequestLabel')}
            </span>
            {isOwn && !editing && (
              <button className="cal-notes-edit-btn" onClick={() => setEditing(true)}>
                {notes ? t('prayer.editNotes') : t('prayer.addNotes')}
              </button>
            )}
          </div>
          {isOwn && notes ? (
            <div className="cal-notes-display" onClick={() => setEditing(true)}>
              <p>{renderNotes(notes)}</p>
              {saved && <span className="cal-notes-saved" style={{display:'inline-flex',alignItems:'center',gap:3}}><Check size={11} strokeWidth={3}/> Saved</span>}
            </div>
          ) : isOwn ? (
            <button className="cal-notes-empty" onClick={() => setEditing(true)}>
              {t('prayer.addNotes')}
            </button>
          ) : local?.description ? (
            <>
              <p className="cal-notes-text" style={{fontStyle:'italic',color:'var(--text-lt)',cursor:'default',margin:'6px 0 0'}}>{local.description}</p>
              <TranslationBadge isTranslated={local.isTranslated} canTranslate={local.canTranslate}
                sourceLang={local.sourceLang} onClick={local.toggle} compact/>
            </>
          ) : null}
        </div>
        {isOwn && (
          <NotesEditorModal
            open={editing}
            initialValue={notes}
            title={t('prayer.editingMyNotes')}
            subtitle={local?.title || wi?.title || ''}
            placeholder={t('prayer.formRequestPlaceholder')}
            saving={saving}
            onSave={saveNotes}
            onClose={() => setEditing(false)}
          />
        )}

        {/* Prayer request updates */}
        {wi?.updates?.length > 0 && (
          <div className="cal-updates-section">
            <div className="cal-notes-header">
              <span className="cal-notes-label" style={{display:'inline-flex',alignItems:'center',gap:5}}>
                <MessageCircle size={13}/> {t('prayer.updatesLabel', wi.updates.length)}
              </span>
            </div>
            {wi.updates.map(u => (
              <LocalizedUpdateRow key={u.id} update={u} variant="cal"/>
            ))}
          </div>
        )}

        {/* AI Sample Prayer — same as EventDetail */}
        <div className="cal-ai-section">
          {showAIPrayer && currentPrayer ? (
            <div className="cal-ai-prayer-block">
              <div className="cal-ai-header">
                <span className="cal-ai-label"><Sparkles size={12}/> {t('prayer.samplePrayer')}</span>
                {prayerHistory.length > 1 && (
                  <div className="cal-ai-nav">
                    <button className="cal-ai-nav-btn" onClick={() => setAiIndex(i => Math.max(0, i - 1))}
                      disabled={safeIndex === 0} aria-label={t('prayer.previousPrayer')}>
                      <ChevronLeft size={15}/>
                    </button>
                    <span className="cal-ai-nav-count">{safeIndex + 1} / {prayerHistory.length}</span>
                    <button className="cal-ai-nav-btn" onClick={() => setAiIndex(i => Math.min(prayerHistory.length - 1, i + 1))}
                      disabled={safeIndex >= prayerHistory.length - 1} aria-label={t('prayer.nextPrayer')}>
                      <ChevronRight size={15}/>
                    </button>
                  </div>
                )}
              </div>
              <p className="cal-ai-text">{currentPrayer}</p>
              <div className="cal-ai-actions-bar">
                <button className="cal-ai-action-btn" onClick={handleCopyPrayer}>
                  {copied ? <><Check size={14} strokeWidth={3}/> {t('prayNow.copied')}</> : <><Copy size={14}/> {t('prayer.copyPrayer')}</>}
                </button>
                <button className="cal-ai-action-btn" onClick={async () => {
                  if (navigator.share) {
                    try { await navigator.share({ title: `Prayer: ${wi?.title||''}`, text: currentPrayer }); } catch {}
                  } else { handleCopyPrayer(); }
                }}>
                  <Share size={14}/> {t('prayer.sharePrayer')}
                </button>
                <button className="cal-ai-action-btn cal-ai-action-regen" onClick={handleGeneratePrayer} disabled={genPraying}>
                  <RefreshCw size={14}/> {genPraying ? '…' : t('prayer.newPrayer')}
                </button>
              </div>
            </div>
          ) : (
            <button className="cal-ai-gen-btn" onClick={handleGeneratePrayer} disabled={genPraying}>
              {genPraying
                ? <><Sparkles size={13}/> {t('prayer.generatingPrayer')}</>
                : <><Sparkles size={13}/> {t('prayer.generatePrayer')}</>}
            </button>
          )}
        </div>

        {/* Pray button */}
        <button
          className={`cal-pray-btn ${localEvent.prayedAt?'prayed':''} ${praying?'praying':''}`}
          onClick={handlePray} disabled={!!localEvent.prayedAt || praying}
          style={localEvent.prayedAt ? {background:wallBg,color:wallColor,cursor:'default'} : {background:wallColor}}>
          {localEvent.prayedAt
            ? <><Check size={16} strokeWidth={3} color="currentColor"/> {t('prayer.prayedButton')}</>
            : praying
              ? <><PrayingHandsIcon size={16} color="currentColor"/> {t('prayer.prayingButton')}</>
              : <><PrayingHandsIcon size={16} color="currentColor"/> {t('prayer.prayButton')}</>}
        </button>
      </div>
    </div>
  );
}


export function DayEventList({ dayKey, events, wallEvents, selectedEventId, onSelectEvent, onWallEventPrayed, currentUserId }) {
  const { t, lang } = useTranslation();
  const date = new Date(dayKey+'T00:00:00');
  const isToday = dayKey === ymd(new Date());
  const dayLabel = date.toLocaleDateString(undefined, {weekday:'long',month:'long',day:'numeric'});

  return (
    <div className="cal-day-list">
      <div className="cal-day-list-header">
        <span className="cal-day-list-title" style={{display:'inline-flex',alignItems:'center',gap:6}}>
          {isToday ? <><CalendarDays size={15}/> {t('common.today')}</> : dayLabel}
        </span>
      </div>
      {events.length === 0 && (wallEvents||[]).length === 0 ? (
        <p className="cal-day-empty">{t('prayer.noPrayersDay')}</p>
      ) : (
        <div className="cal-day-events">
          {events.map(e => {
            const r = e.recipient;
            const meta = PRIORITY_META[r?.priority] || PRIORITY_META.normal;
            const isSelected = e.id === selectedEventId;
            return (
              <button key={e.id}
                className={`cal-event-btn ${isSelected?'selected':''} ${e.prayedAt?'prayed':''} ${r?.isMemorial?'memorial':''}`}
                style={r?.isMemorial
                  ? {'--accent':'#8b5cf6','--accent-bg':'rgba(139,92,246,0.08)'}
                  : {'--accent': meta.color, '--accent-bg': meta.bg}}
                onClick={() => onSelectEvent(isSelected ? null : e)}>
                <div className="cal-event-dot-line" style={{background: r?.isMemorial ? '#8b5cf6' : meta.color}}/>
                <div className="cal-event-info">
                  {r?.isMemorial
                    ? <><span className="cal-event-name">🕯️ {t('prayer.memorial.calEventLabel')} {r?.name}</span>
                        <span className="cal-event-rel" style={{color:'#8b5cf6'}}>{t('prayer.memorial.calEventSub')}</span></>
                    : <><span className="cal-event-name">{t('prayer.prayFor')} {r?.name}</span>
                        {r?.relationship && <span className="cal-event-rel">{(() => { const idx = RELATIONSHIPS_EN.indexOf(r.relationship); const rels = t('prayer.relationships'); return (Array.isArray(rels) && idx >= 0) ? rels[idx] : r.relationship; })()}</span>}</>
                  }
                </div>
                <div className="cal-event-right">
                  <EventAvatar
                    src={r?.linkedUser?.avatar}
                    name={r?.name}
                    memorial={r?.isMemorial}
                    ringColor={r?.isMemorial ? '#8b5cf6' : meta.color}
                    prayed={!!e.prayedAt}
                  />
                </div>
              </button>
            );
          })}
          {(wallEvents||[]).map(e => {
            const wi = e.wallItem;
            const isOwn = wi?.userId === currentUserId;
            const name  = wi?.user?.name || 'Prayer Request';
            const isSelected = e.id === selectedEventId;
            const wallColor = isOwn ? WALL_OWN_COLOR : WALL_NET_COLOR;
            const wallBg    = isOwn ? WALL_OWN_BG    : WALL_NET_BG;
            const showAlt   = wi?.sourceLang && wi.sourceLang !== lang && wi.titleAlt;
            const dispTitle = showAlt ? wi.titleAlt : wi?.title;
            const wallEmoji = CATEGORY_ICONS[wi?.category] || '🙏';
            return (
              <button key={e.id}
                className={`cal-event-btn cal-event-wall ${isSelected?'selected':''} ${e.prayedAt?'prayed':''}`}
                style={{'--accent': wallColor, '--accent-bg': wallBg}}
                onClick={() => onSelectEvent(isSelected ? null : {...e, _isWall:true})}>
                <div className="cal-event-dot-line" style={{background: wallColor}}/>
                <div className="cal-event-info">
                  <span className="cal-event-name">🙏 {dispTitle}</span>
                  <span className="cal-event-rel" style={{color: wallColor}}>{isOwn ? t('prayer.calMyRequest') : t('prayer.fromNetwork', name)}</span>
                </div>
                <div className="cal-event-right">
                  <EventAvatar
                    emoji={wallEmoji}
                    name={name}
                    ringColor={wallColor}
                    prayed={!!e.prayedAt}
                  />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Pray on Date Modal ────────────────────────────────────────────────────────
// Exported because the People list offers the same "add to calendar" action
// from a person's row.
export function PrayOnDateModal({ recipient, onClose, onConfirm }) {
  const { t } = useTranslation();
  const tomorrowStr = ymd(new Date(Date.now() + 86400000));
  const [date, setDate] = useState(tomorrowStr);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!date) { setErr(t('prayer.chooseDate')); return; }
    setBusy(true); setErr('');
    try { await onConfirm(recipient.id, date); onClose(); }
    catch(ex) { setErr(ex.message || t('common.error')); }
    finally { setBusy(false); }
  }

  return ReactDOM.createPortal(
    <div className="prr-modal" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="prr-modal-backdrop" onClick={onClose}/>
      <div className="prr-modal-dialog" role="dialog" aria-modal="true">
        <div className="prr-modal-header">
          <div className="prr-modal-title">{t('prayer.addToCalendar')}</div>
          <button className="prr-modal-x" onClick={onClose} aria-label={t('common.close')}><X size={18}/></button>
        </div>
        <form className="prr-modal-body" onSubmit={handleSubmit}>
          <label className="prr-modal-label" htmlFor="prr-date-input">{t('prayer.addToCalendar')}</label>
          <input id="prr-date-input" type="date" className="prr-modal-date"
            value={date} min={tomorrowStr} onChange={e => setDate(e.target.value)} required/>
          {err && <div className="prr-modal-help">{err}</div>}
          <div className="prr-modal-actions">
            <button type="button" className="prr-modal-btn prr-modal-cancel" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className={`prr-modal-btn prr-modal-submit${busy?' is-busy':''}`} disabled={busy}>
              {busy ? t('prayer.addingToCalendar') : t('prayer.addToCalendar')}
            </button>
          </div>
        </form>
      </div>
    </div>, document.body
  );
}

export function Toast({ message, onHide }) {
  useEffect(() => { const timer = setTimeout(onHide, 2800); return () => clearTimeout(timer); }, [onHide]);
  return ReactDOM.createPortal(<div className="prr-toast">{message}</div>, document.body);
}

// Six-phase discipleship pipeline mapped to the Four Chairs framework.
// Phases 1-3 (till/soil/seed) are pre-faith (Chair 1: Come & See).
// Phases 4-6 (sprout/plant/harvest) are post-faith (Chairs 2-4).
const WITNESS_FLOW            = ['till', 'soil', 'seed', 'sprout', 'plant', 'harvest'];
const NON_BELIEVER_STAGES     = ['till', 'soil', 'seed'];
const BELIEVER_STAGES_SET     = new Set(['sprout', 'plant', 'harvest']);
const STAGE_ICONS = {
  till: Shovel, soil: Droplet, seed: Sprout,
  sprout: Leaf, plant: TreeDeciduous, harvest: Wheat,
};
// Oikos = people you're reaching — anyone on a non-believer chair,
// excluding memorials. The chair is the single source of truth for
// faith status; there is no separate hasFaith field.
export function isOikos(r) {
  const stage = WITNESS_FLOW.includes(r?.witnessStage) ? r.witnessStage : 'till';
  return !!r && !r.isMemorial && !BELIEVER_STAGES_SET.has(stage);
}

// First name from a recipient's full name — used to fill invite templates.
function firstName(name) { return (name || '').trim().split(/\s+/)[0] || name || ''; }

// ── Quick-add relationship data ──────────────────────────────────────────────
// Removed as collateral when RecipientCard was deleted, which left
// QuickAddRecipientsModal throwing on render. Restored verbatim.
//
// `single: true` means there is normally only one of these in a life (one
// mother, one husband), so the quick-add grid offers a single slot rather than
// an add-another control. `value` must match RELATIONSHIPS_EN exactly — it is
// what gets stored on the recipient.
const QA_RELATIONSHIP_DEFS = [
  { value:'Husband',        group:'immediate', single:true  },
  { value:'Wife',           group:'immediate', single:true  },
  { value:'Fiancé',         group:'immediate', single:true  },
  { value:'Mother',         group:'immediate', single:true  },
  { value:'Step Mother',    group:'immediate', single:true  },
  { value:'Father',         group:'immediate', single:true  },
  { value:'Step Father',    group:'immediate', single:true  },
  { value:'Son',            group:'immediate', single:false },
  { value:'Step Son',       group:'immediate', single:false },
  { value:'Daughter',       group:'immediate', single:false },
  { value:'Step Daughter',  group:'immediate', single:false },
  { value:'Brother',        group:'immediate', single:false },
  { value:'Step Brother',   group:'immediate', single:false },
  { value:'Sister',         group:'immediate', single:false },
  { value:'Step Sister',    group:'immediate', single:false },
  { value:'Grandmother',    group:'extended',  single:true  },
  { value:'Grandfather',    group:'extended',  single:true  },
  { value:'Mother-in-law',  group:'extended',  single:true  },
  { value:'Father-in-law',  group:'extended',  single:true  },
  { value:'Cousin',         group:'extended',  single:false },
  { value:'Uncle',          group:'extended',  single:false },
  { value:'Aunt',           group:'extended',  single:false },
  { value:'Nephew',         group:'extended',  single:false },
  { value:'Niece',          group:'extended',  single:false },
  { value:'Grandson',       group:'extended',  single:false },
  { value:'Granddaughter',  group:'extended',  single:false },
  { value:'Brother-in-law', group:'extended',  single:false },
  { value:'Sister-in-law',  group:'extended',  single:false },
  { value:'Son-in-law',     group:'extended',  single:false },
  { value:'Daughter-in-law',group:'extended',  single:false },
  { value:'Boyfriend',      group:'friends',   single:true  },
  { value:'Girlfriend',     group:'friends',   single:true  },
  { value:'Employer',       group:'friends',   single:true  },
  { value:'Best Friend',    group:'friends',   single:false },
  { value:'Close Friend',   group:'friends',   single:false },
  { value:'Friend',         group:'friends',   single:false },
  { value:'Neighbor',       group:'friends',   single:false },
  { value:'Co-worker',      group:'friends',   single:false },
  { value:'Employee',       group:'friends',   single:false },
  { value:'Acquaintance',   group:'friends',   single:false },
  { value:'Stranger',       group:'friends',   single:false },
  { value:'Church Family',  group:'friends',   single:false },
  { value:'Classmate',      group:'friends',   single:false },
  { value:'Pastor',         group:'friends',   single:false },
  { value:'Mentor',         group:'friends',   single:false },
  { value:'Mentee',         group:'friends',   single:false },
];

const QA_GROUP_META = {
  immediate: { label:'Immediate Family', color:'#1B4F8A', darkBg:'rgba(27,79,138,0.1)'   },
  extended:  { label:'Extended Family',  color:'#0e9a8a', darkBg:'rgba(14,154,138,0.1)'  },
  friends:   { label:'Friends & Others', color:'#6366f1', darkBg:'rgba(99,102,241,0.1)'  },
};

export function QuickAddRecipientsModal({ onClose, onSaved }) {
  const { t: tp } = useTranslation('prayer');
  const { t } = useTranslation();
  const translatedRels = tp('relationships');
  const labelMap = {};
  RELATIONSHIPS_EN.forEach((en, i) => {
    labelMap[en] = Array.isArray(translatedRels) ? (translatedRels[i] || en) : en;
  });

  const [entries, setEntries] = useState({});
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  function toggle(value) {
    setEntries(prev => {
      if (prev[value]) { const n = {...prev}; delete n[value]; return n; }
      return { ...prev, [value]: [''] };
    });
  }
  function addAnother(value) {
    setEntries(prev => ({ ...prev, [value]: [...(prev[value] || []), ''] }));
  }
  function updateName(value, idx, val) {
    setEntries(prev => { const a = [...(prev[value] || [])]; a[idx] = val; return {...prev, [value]: a}; });
  }
  function removeName(value, idx) {
    setEntries(prev => {
      const a = [...(prev[value] || [])]; a.splice(idx, 1);
      if (a.length === 0) { const n = {...prev}; delete n[value]; return n; }
      return {...prev, [value]: a};
    });
  }
  function countFilled() {
    return Object.values(entries).flat().filter(n => n.trim()).length;
  }

  async function handleSave() {
    const recipients = [];
    for (const [rel, names] of Object.entries(entries))
      for (const name of names)
        if (name.trim()) recipients.push({ name: name.trim(), relationship: rel });
    if (recipients.length === 0) { onClose(); return; }
    setSaving(true); setError('');
    try {
      await api.post('/api/prayer/bulk', { recipients });
      onSaved(recipients.length);
    } catch { setError('Could not save. Please try again.'); }
    finally { setSaving(false); }
  }

  const filled = countFilled();

  return (
    <div className="pw-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pw-modal pw-modal-wide fade-up">
        <div className="pw-modal-header">
          <h3>{t('prayer.quickAddRecipients')}</h3>
          <button className="pw-modal-close" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="pw-modal-body">
          <p className="qa-intro">
            <Lock size={12}/> {t('prayer.formRequestPlaceholder') ? t('community.biblePlan.noPlansOrg') : 'Tap a relationship to add someone.'}
          </p>
          {error && <div className="pw-error"><AlertTriangle size={14}/> {error}</div>}
          {['immediate','extended','friends'].map(groupKey => {
            const meta = QA_GROUP_META[groupKey];
            const rels = QA_RELATIONSHIP_DEFS.filter(r => r.group === groupKey);
            const activeInGroup = rels.filter(r => entries[r.value]);
            return (
              <div key={groupKey} className="qa-group">
                <div className="qa-group-label" style={{color: meta.color}}>
                  <span>{meta.label}</span>
                  {activeInGroup.length > 0 && (
                    <span className="qa-group-count-badge" style={{background: meta.color}}>
                      {activeInGroup.length}
                    </span>
                  )}
                  {activeInGroup.length > 0 && (
                    <button className="qa-group-clear" onClick={() => {
                      activeInGroup.forEach(r => setEntries(prev => {
                        const n = {...prev}; delete n[r.value]; return n;
                      }));
                    }}>Clear</button>
                  )}
                </div>
                <div className="qa-masonry">
                  {rels.map(rel => {
                    const active = !!entries[rel.value];
                    const display = labelMap[rel.value] || rel.value;
                    return (
                      <div key={rel.value} className={`qa-chip-card${active ? ' active' : ''}`}
                        style={active
                          ? {borderColor: meta.color, borderLeftColor: meta.color, background: meta.darkBg}
                          : {borderLeftColor: meta.color + '55'}}>
                        {/* Chip header — click to toggle */}
                        <button className="qa-chip-header"
                          style={active ? {color: meta.color} : {}}
                          onClick={() => toggle(rel.value)}>
                          <div className={`qa-chip-check${active ? ' checked' : ''}`}
                            style={active ? {background: meta.color, borderColor: meta.color} : {}}>
                            {active && <Check size={9} strokeWidth={3} color="white"/>}
                          </div>
                          <span className="qa-chip-label">{display}</span>
                        </button>
                        {/* Inline name inputs — shown when active */}
                        {active && (
                          <div className="qa-chip-inputs" onClick={e => e.stopPropagation()}>
                            {(entries[rel.value] || []).map((name, idx) => (
                              <div key={idx} className="qa-name-row">
                                <input className="qa-name-input"
                                  placeholder={`${display} name…`}
                                  value={name}
                                  onChange={e => updateName(rel.value, idx, e.target.value)}
                                  autoFocus={idx === (entries[rel.value].length - 1)}/>
                                {entries[rel.value].length > 1 && (
                                  <button className="qa-remove-btn" onClick={() => removeName(rel.value, idx)}>
                                    <X size={13}/>
                                  </button>
                                )}
                              </div>
                            ))}
                            {!rel.single && (
                              <button className="qa-add-another" style={{color: meta.color}}
                                onClick={() => addAnother(rel.value)}>
                                <Plus size={11}/> Add another
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="pw-modal-footer">
          <button className="pw-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="pw-btn-save" onClick={handleSave} disabled={saving || filled === 0}>
            {saving
              ? <span className="spinner" style={{width:16,height:16}}/>
              : <><Check size={15}/> Save {filled > 0 ? `${filled} person${filled !== 1 ? 's' : ''}` : 'recipients'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Prayer Wall components (merged from PrayerWallScreen)
// ─────────────────────────────────────────────────────────────────────────────

export function AddEditWallModal({ item, onClose, onSave }) {
  const { t: tw } = useTranslation('prayerWall');
  const categories = tw('categories');
  const [title,setTitle]=useState(item?.title||'');
  const [category,setCategory]=useState(item?.category||CATEGORIES_EN[0]);
  const [description,setDescription]=useState(item?.description||'');
  const [endDate,setEndDate]=useState(item?.endDate?item.endDate.slice(0,10):'');
  const [saving,setSaving]=useState(false);
  const [error,setError]=useState('');
  const [joinedComms,setJoinedComms]=useState([]);

  // Derive initial vis modes from existing item
  function initVisModes() {
    const v = item?.visibility || 'private';
    if (v === 'connections+groups') return new Set(['connections', 'groups']);
    // normalize any unknown value to private
    if (!['private','connections','groups','all'].includes(v)) return new Set(['private']);
    return new Set([v]);
  }
  const [visModes,setVisModes]=useState(initVisModes);
  const [targetGroupIds,setTargetGroupIds]=useState(item?.targetGroupIds||[]);

  // Load joined communities for the community picker
  useEffect(()=>{
    api.get('/api/communities?joined=true')
      .then(d=>setJoinedComms(d.communities||[]))
      .catch(()=>{});
  },[]);

  // Derive the visibility string to send to API
  function computeVisibility() {
    if (visModes.has('private')) return 'private';
    if (visModes.has('all'))     return 'all';
    if (visModes.has('connections') && visModes.has('groups')) return 'connections+groups';
    if (visModes.has('connections')) return 'connections';
    if (visModes.has('groups'))      return 'groups';
    return 'private';
  }

  function toggleMode(mode) {
    setVisModes(prev => {
      const next = new Set(prev);
      if (mode === 'private' || mode === 'all') {
        // If already the only active mode, do nothing (must have a selection)
        if (next.has(mode) && next.size === 1) return next;
        // Otherwise switch to this exclusive mode, clear everything else
        return new Set([mode]);
      }
      // connections / groups — combinable
      // Switching away from an exclusive mode: clear it first
      next.delete('private');
      next.delete('all');
      if (next.has(mode)) {
        next.delete(mode);
        if (mode === 'groups') setTargetGroupIds([]);
        if (next.size === 0) next.add('private'); // fallback
      } else {
        next.add(mode);
      }
      return next;
    });
  }

  function toggleGroup(id) {
    setTargetGroupIds(prev =>
      prev.includes(id) ? prev.filter(x=>x!==id) : [...prev,id]
    );
  }

  async function handleSubmit() {
    if(!title.trim()){setError('Please enter a title.');return;}
    setSaving(true);setError('');
    try{
      const visibility = computeVisibility();
      const payload={
        title:title.trim(), category,
        description:description.trim()||null,
        endDate:endDate||null,
        visibility,
        targetGroupIds: (visibility==='groups'||visibility==='connections+groups') ? targetGroupIds : [],
      };
      const saved=item?await prayerWallApi.update(item.id,payload):await prayerWallApi.create(payload);
      onSave(saved.item,!!item);
    }catch(e){setError(e.message||'Could not save.');}
    finally{setSaving(false);}
  }

  const showGroupPicker = visModes.has('groups');

  // `all` reaches your connections plus every member of your communities
  // (see visibilityConditions in routes/prayerWall.js) — never anyone outside
  // that. It was labelled "Public · Anyone on Ignite", which promised a reach
  // it does not have; the request card already called it by its real name.
  const VIS_OPTIONS = [
    { mode:'private',     Icon:Lock,   label:tw('formVis.private'),     desc:tw('formVis.privateDesc') },
    { mode:'connections', Icon:Users,  label:tw('formVis.connections'), desc:tw('formVis.connectionsDesc') },
    { mode:'groups',      Icon:Users,  label:tw('formVis.groups'),      desc:tw('formVis.groupsDesc') },
    { mode:'all',         Icon:Globe,  label:tw('formVis.all'),         desc:tw('formVis.allDesc') },
  ];

  return (
    <div className="pw-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="pw-modal fade-up">
        <div className="pw-modal-header">
          <h3>{item?tw('editRequest'):tw('newRequest')}</h3>
          <button className="pw-modal-close" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="pw-modal-body">
          {error&&<div className="pw-error"><AlertTriangle size={14}/> {error}</div>}
          <div className="pw-field">
            <label className="pw-label">{tw('titleLabel')} <span className="pw-required">*</span></label>
            <input className="pw-input" autoFocus placeholder={tw('titlePlaceholder')}
              value={title} onChange={e=>setTitle(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSubmit()}/>
          </div>
          <div className="pw-field">
            <label className="pw-label"><Tag size={13}/> {tw('categoryLabel')}</label>
            <div className="pw-category-grid">
              {(Array.isArray(categories)?categories:CATEGORIES_EN).map((cat,i)=>{
                const enCat=CATEGORIES_EN[i]||cat;
                const active=category===enCat;
                return(<button key={enCat} className={`pw-cat-chip${active?' active':''}`} onClick={()=>setCategory(enCat)}>
                  <span className="pw-cat-icon">{CATEGORY_ICONS[enCat]||'📌'}</span><span>{cat}</span>
                </button>);
              })}
            </div>
          </div>
          <div className="pw-field">
            <label className="pw-label"><BookOpen size={13}/> {tw('descriptionLabel')}</label>
            <textarea className="pw-input pw-textarea" rows={3} placeholder={tw('descriptionPlaceholder')}
              value={description} onChange={e=>setDescription(e.target.value)}/>
          </div>
          <div className="pw-field">
            <label className="pw-label"><CalendarDays size={13}/> {tw('endDateLabel')}</label>
            <input type="date" className="pw-input" value={endDate} onChange={e=>setEndDate(e.target.value)}/>
            <span className="pw-hint">{tw('endDateHint')}</span>
          </div>
          <div className="pw-field">
            <label className="pw-label">{tw('visibilityLabel')}</label>
            <div className="pw-vis-options">
              {VIS_OPTIONS.map(opt=>{
                const isActive = visModes.has(opt.mode);
                return (
                  <button key={opt.mode}
                    className={`pw-vis-option${isActive?' active':''}`}
                    onClick={()=>toggleMode(opt.mode)}>
                    <div className="pw-vis-option-icon">
                      <opt.Icon size={15}/>
                    </div>
                    <div className="pw-vis-option-text">
                      <span className="pw-vis-option-label">{opt.label}</span>
                      <span className="pw-vis-option-desc">{opt.desc}</span>
                    </div>
                    <div className={`pw-vis-option-check${isActive?' checked':''}`}>
                      {isActive&&<Check size={11} strokeWidth={3}/>}
                    </div>
                  </button>
                );
              })}
            </div>
            {showGroupPicker && (
              <div className="pw-group-picker">
                <p className="pw-group-picker-hint">
                  {targetGroupIds.length===0
                    ? 'Sharing with all your communities. Select specific ones to limit who sees this.'
                    : `Sharing with ${targetGroupIds.length} communit${targetGroupIds.length===1?'y':'ies'}.`}
                </p>
                {joinedComms.length===0
                  ? <p className="pw-group-picker-empty">You haven't joined any communities yet.</p>
                  : joinedComms.map(comm=>{
                    const sel = targetGroupIds.includes(comm.id);
                    return (
                      <button key={comm.id}
                        className={`pw-group-chip${sel?' selected':''}`}
                        onClick={()=>toggleGroup(comm.id)}>
                        <span className="pw-group-chip-emoji">{comm.emoji||'👥'}</span>
                        <span className="pw-group-chip-name">{comm.name}</span>
                        {sel&&<Check size={12} strokeWidth={3} style={{marginLeft:'auto',flexShrink:0}}/>}
                      </button>
                    );
                  })
                }
              </div>
            )}
          </div>
        </div>
        <div className="pw-modal-footer">
          <button className="pw-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="pw-btn-save" onClick={handleSubmit} disabled={saving}>
            {saving?<span className="spinner" style={{width:16,height:16}}/>:<><Check size={15}/> {tw('saveRequest')}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function AnswerWallModal({ item, onClose, onAnswered }) {
  const { t: tw } = useTranslation('prayerWall');
  const [note,setNote]=useState('');const [saving,setSaving]=useState(false);const [error,setError]=useState('');
  async function handleSubmit() {
    setSaving(true);setError('');
    try{const{item:updated}=await prayerWallApi.answer(item.id,note);onAnswered(updated);}
    catch(e){setError(e.message||'Could not mark as answered.');}
    finally{setSaving(false);}
  }
  return (
    <div className="pw-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="pw-modal pw-answer-modal fade-up">
        <div className="pw-answer-header">
          <div className="pw-answer-icon">🙌</div>
          <h3>{tw('answeredTitle')}</h3>
          <p className="pw-answer-subtitle">"{item.title}"</p>
          <button className="pw-modal-close" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="pw-modal-body">
          {error&&<div className="pw-error"><AlertTriangle size={14}/> {error}</div>}
          <div className="pw-field">
            <label className="pw-label">{tw('answeredLabel')}</label>
            <textarea className="pw-input pw-textarea pw-answer-textarea" rows={5} autoFocus
              placeholder={tw('answeredPlaceholder')} value={note} onChange={e=>setNote(e.target.value)}/>
          </div>
        </div>
        <div className="pw-modal-footer">
          <button className="pw-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="pw-btn-answered" onClick={handleSubmit} disabled={saving}>
            {saving?<span className="spinner" style={{width:16,height:16}}/>:<><MoveRight size={15}/> {tw('moveToThankYou')}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function SwipeCard({ item, onSwipe, isTop }) {
  const startX=useRef(0),currentX=useRef(0),dragging=useRef(false);
  const [offset,setOffset]=useState(0);
  const [decided,setDecided]=useState(null);
  const local = useLocalizedItem(item);
  const THRESHOLD=80;
  function getX(e){return e.touches?e.touches[0].clientX:e.clientX;}
  function onStart(e){if(!isTop||decided)return;startX.current=getX(e);currentX.current=0;dragging.current=true;}
  function onMove(e){if(!dragging.current||!isTop)return;currentX.current=getX(e)-startX.current;setOffset(currentX.current);}
  function onEnd(){
    if(!dragging.current||!isTop)return;dragging.current=false;
    const x=currentX.current;
    if(x>THRESHOLD){setDecided('add');setTimeout(()=>onSwipe(item.id,'added'),350);}
    else if(x<-THRESHOLD){setDecided('dismiss');setTimeout(()=>onSwipe(item.id,'dismissed'),350);}
    else setOffset(0);
  }
  const addOp=Math.min(1,Math.max(0,offset/THRESHOLD));
  const dimOp=Math.min(1,Math.max(0,-offset/THRESHOLD));
  const icon=CATEGORY_ICONS[item.category]||'📌';
  return (
    <div className={`pw-swipe-card${decided?` pw-swipe-exit-${decided}`:''}`}
      style={{transform:`translateX(${offset}px) rotate(${offset/15}deg)`,
              transition:dragging.current?'none':'transform 0.35s cubic-bezier(.25,.46,.45,.94)',
              cursor:isTop?'grab':'default',zIndex:isTop?10:5}}
      onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
      onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}>
      <div className="pw-swipe-indicator pw-swipe-add" style={{opacity:addOp}}>🙏 Praying</div>
      <div className="pw-swipe-indicator pw-swipe-dismiss" style={{opacity:dimOp}}>✕ Pass</div>
      <div className="pw-swipe-tape"/>
      <div className="pw-swipe-inner">
        <div className="pw-swipe-category">
          <span>{icon}</span><span>{item.category}</span>
          {item.isSystemItem && (
            <span className="pw-system-badge">✦ Ignite</span>
          )}
        </div>
        <h3 className="pw-swipe-title">{local.title}</h3>
        {local.description&&<p className="pw-swipe-desc">{local.description}</p>}
        <TranslationBadge isTranslated={local.isTranslated} canTranslate={local.canTranslate}
          sourceLang={local.sourceLang} onClick={local.toggle}/>
        {item.isSystemItem ? (
          <div className="pw-swipe-author">
            <div className="pw-swipe-avatar pw-system-avatar">✦</div>
            <div>
              <div className="pw-swipe-author-name">Ignite App</div>
              <div className="pw-swipe-author-sub">Community Prayer</div>
            </div>
          </div>
        ) : (
          <div className="pw-swipe-author">
            {item.user?.avatar
              ?<img src={item.user.avatar} alt="" className="pw-swipe-avatar" loading="lazy"/>
              :<div className="pw-swipe-avatar pw-swipe-avatar-placeholder">{item.user?.name?.[0]||'?'}</div>}
            <div>
              <div className="pw-swipe-author-name">{shortName(item.user?.name) || 'Anonymous'}</div>
              <div className="pw-swipe-author-sub">{fmtDateWall(item.createdAt)}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function DiscoverTabContent({ onAddRequest, highlightItemId, onSwiped }) {
  const { t } = useTranslation();
  const [cards,setCards]=useState([]);
  const [loading,setLoading]=useState(true);
  const [mode,setMode]=useState('new'); // 'new' | 'review' (revisiting passed-on requests)
  const [skippedCards,setSkippedCards]=useState([]);
  const skippedFetched=useRef(false);
  const { refreshDots } = useNavDots();
  useEffect(()=>{
    prayerWallApi.discover().then(d=>{
      let items=d.items||[];
      if(highlightItemId){const idx=items.findIndex(i=>i.id===highlightItemId);if(idx>0){const[item]=items.splice(idx,1);items=[item,...items];}}
      setCards(items);
    }).catch(()=>{}).finally(()=>setLoading(false));
  },[highlightItemId]);
  const empty = !loading && !cards.length;
  // When the new feed runs out, fetch the user's passed-on items once so we can
  // offer a "Revisit passed requests" button — only when some actually exist.
  useEffect(()=>{
    if(empty && mode==='new' && !skippedFetched.current){
      skippedFetched.current=true;
      prayerWallApi.discoverSkipped().then(d=>setSkippedCards(d.items||[])).catch(()=>{});
    }
  },[empty,mode]);
  async function handleSwipe(itemId,action){
    await prayerWallApi.swipe(itemId,action).catch(()=>{});
    setCards(prev=>{
      const next=prev.filter(c=>c.id!==itemId);
      if(onSwiped) onSwiped(next.length); // tell parent how many remain
      if(!next.length) refreshDots();
      return next;
    });
  }
  function startReview(){
    setCards(skippedCards);
    setSkippedCards([]); // one review pass per session — don't immediately re-offer
    setMode('review');
  }
  if(loading)return<div className="pw-loading"><span className="spinner" style={{width:32,height:32}}/></div>;
  if(empty){
    const reviewing = mode==='review';
    return(
      <div className="pw-discover-empty">
        <div className="pw-discover-empty-icon">🕊️</div>
        <h3>{t(reviewing?'prayer.discoverReviewEmpty':'prayer.discoverEmpty')}</h3>
        <p>{t(reviewing?'prayer.discoverReviewEmptyDesc':'prayer.discoverEmptyDesc')}</p>
        {!reviewing && skippedCards.length>0 && (
          <button className="btn pw-discover-review-btn" onClick={startReview}>
            <RotateCcw size={15}/> {t('prayer.discoverReviewBtn')}
          </button>
        )}
        <button className="btn btn-primary" style={{marginTop:12,gap:6}} onClick={onAddRequest}>
          <Plus size={15}/> {t('prayer.addPrayerRequest')}
        </button>
      </div>
    );
  }
  return(
    <div className="pw-discover">
      <div className="pw-discover-header">
        {mode==='review'
          ? <p className="pw-discover-count pw-discover-review-label"><RotateCcw size={13}/> {t('prayer.discoverReviewMode')} · {cards.length}</p>
          : <p className="pw-discover-count">{cards.length} request{cards.length!==1?'s':''} from your network</p>}
        <div className="pw-swipe-hints">
          <span className="pw-hint-dismiss">← Pass</span>
          <span className="pw-hint-add">Pray →</span>
        </div>
      </div>
      <div className="pw-card-stack">
        {cards.slice(0,3).map((card,i)=>(
          <SwipeCard key={card.id} item={card} isTop={i===0} onSwipe={handleSwipe}/>
        )).reverse()}
      </div>
      <div className="pw-swipe-actions">
        <button className="pw-swipe-action-btn pw-swipe-action-dismiss" onClick={()=>cards[0]&&handleSwipe(cards[0].id,'dismissed')}>
          <X size={22}/><span>Pass</span>
        </button>
        <button className="pw-swipe-action-btn pw-swipe-action-add" onClick={()=>cards[0]&&handleSwipe(cards[0].id,'added')}>
          <HandHeart size={22}/><span>I'll pray</span>
        </button>
      </div>
    </div>
  );
}

function StickyNote({ item, currentUserId, onEdit, onDelete, onMarkAnswered, status }) {
  const { t: tw } = useTranslation('prayerWall');
  const { t } = useTranslation();
  const local = useLocalizedItem(item);
  const [expanded,setExpanded]=useState(false);
  const [totalCount,setTotalCount]=useState(item.totalPrayerCount||0);
  const [myCount] = useState(item.myPrayerCount||0);
  const [menuOpen,setMenuOpen]=useState(false);
  const [addingCal,setAddingCal]=useState(false);
  const [showDatePicker,setShowDatePicker]=useState(false);
  const [pickedDate,setPickedDate]=useState('');
  const [calAdded,setCalAdded]=useState(false);
  const [showReport,setShowReport]=useState(false);
  const isOwner=item.user?.id===currentUserId;
  const isAnswered=status==='thankyou';
  const menuRef=useRef(null);
  useEffect(()=>{
    if(!menuOpen)return;
    function h(e){if(menuRef.current&&!menuRef.current.contains(e.target))setMenuOpen(false);}
    document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);
  },[menuOpen]);
  async function handleAddToday(){
    setMenuOpen(false);setAddingCal(true);
    try{await prayerWallApi.addToCalendar(item.id);setCalAdded(true);}
    catch(e){alert(e.message||'Could not add to calendar');}
    finally{setAddingCal(false);}
  }
  async function handleAddDate(){
    if(!pickedDate)return;setAddingCal(true);
    try{await prayerWallApi.addToCalendarDate(item.id,pickedDate);setCalAdded(true);setShowDatePicker(false);}
    catch(e){alert(e.message||'Could not add to calendar');}
    finally{setAddingCal(false);}
  }
  const visBaseLabel = {
    private: tw('visPrivate'),
    connections: tw('visConnections'),
    groups: tw('visGroups'),
    'connections+groups': `${tw('visConnections')} + ${tw('visGroups')}`,
    all: tw('visAll'),
  }[item.visibility] || tw('visPrivate');

  // Show specific community count when targetGroupIds is set
  const visLabel = (() => {
    const hasGroups = item.visibility === 'groups' || item.visibility === 'connections+groups';
    const ids = item.targetGroupIds || [];
    if (hasGroups && ids.length > 0) {
      const groupPart = ids.length === 1 ? '1 community' : `${ids.length} communities`;
      if (item.visibility === 'connections+groups') return `Connections + ${groupPart}`;
      return groupPart;
    }
    return visBaseLabel;
  })();
  const categoryIdx=CATEGORIES_EN.indexOf(item.category);
  const categoriesT=tw('categories');
  const categoryDisplay=(Array.isArray(categoriesT)&&categoryIdx>=0)?categoriesT[categoryIdx]:(item.category||'');
  const icon=CATEGORY_ICONS[item.category]||'📌';
  const endDatePassed=item.endDate&&new Date(item.endDate)<new Date();
  return(
    <div className={`pw-note${isAnswered?' pw-note-answered':''}${!isOwner?' pw-note-shared':''}`}>
      <div className="pw-tape"/>
      <div className="pw-note-inner">
        <div className="pw-note-category"><span className="pw-note-cat-icon">{icon}</span><span className="pw-note-cat-label">{categoryDisplay}</span></div>
        <h4 className="pw-note-title">{local.title}</h4>
        {local.description&&(
          <div className={`pw-note-desc${expanded?' expanded':''}`}>
            <p>{local.description}</p>
            {local.description.length>120&&(
              <button className="pw-note-expand" onClick={()=>setExpanded(e=>!e)}>
                {expanded?<><ChevronUp size={12}/> Less</>:<><ChevronDown size={12}/> More</>}
              </button>
            )}
          </div>
        )}
        <TranslationBadge isTranslated={local.isTranslated} canTranslate={local.canTranslate}
          sourceLang={local.sourceLang} onClick={local.toggle} compact/>
        {isAnswered&&local.answeredNote&&(
          <div className="pw-note-testimony">
            <div className="pw-note-testimony-label"><CheckCircle2 size={12}/> {tw('answeredLabel')}</div>
            <p>{local.answeredNote}</p>
          </div>
        )}
        <div className="pw-note-meta">
          {item.endDate&&(<span className={`pw-note-meta-item${endDatePassed&&!isAnswered?' overdue':''}`}><Clock size={11}/> {tw(isAnswered?'answeredOn':'endsOn')} {fmtDateWall(isAnswered?item.answeredAt:item.endDate)}</span>)}
          {isAnswered&&item.answeredAt&&!item.endDate&&(<span className="pw-note-meta-item"><CheckCircle2 size={11}/> {tw('answeredOn')} {fmtDateWall(item.answeredAt)}</span>)}
          {!isOwner&&<span className="pw-note-meta-item pw-note-shared-by"><Heart size={11}/> {shortName(item.user?.name)}</span>}
          {isOwner && item.visibility === 'private' && (
            <span className="pw-note-meta-item"><Lock size={11}/> {tw('privateLabel')}</span>
          )}
          {isOwner && item.visibility !== 'private' && (
            <span className="pw-note-meta-item"><Globe size={11}/> {visLabel}</span>
          )}
        </div>
        {!isAnswered&&(
          <div className="pw-note-pray-row">
            <div className="pw-cal-menu-wrap" ref={menuRef}>
              <button className={`pw-pray-btn${calAdded?' prayed':''}`}
                onClick={()=>!calAdded&&setMenuOpen(o=>!o)} disabled={addingCal||calAdded}>
                {addingCal?<span className="spinner" style={{width:12,height:12,borderWidth:1.5}}/>
                  :calAdded?<><Check size={12}/> {t('prayer.addedToCalendarShort')}</>
                  :<><CalendarDays size={12}/> {t('prayer.addToCalendarBtn')}</>}
              </button>
              {menuOpen&&(
                <div className="pw-cal-menu">
                  <button className="pw-cal-menu-item" onClick={handleAddToday}><CalendarDays size={13}/> {t('prayer.prayToday')}</button>
                  <button className="pw-cal-menu-item" onClick={()=>{setShowDatePicker(true);setMenuOpen(false);}}><CalendarDays size={13}/> {t('prayer.pickADay')}</button>
                </div>
              )}
              {showDatePicker&&(
                <div className="pw-cal-date-picker">
                  <input type="date" className="pw-input" style={{fontSize:13,padding:'5px 8px'}}
                    value={pickedDate} min={new Date().toISOString().slice(0,10)} onChange={e=>setPickedDate(e.target.value)}/>
                  <button className="pw-btn-save" style={{padding:'5px 12px',fontSize:12}} onClick={handleAddDate} disabled={!pickedDate||addingCal}>
                    {addingCal?<span className="spinner" style={{width:11,height:11,borderColor:'white',borderTopColor:'transparent'}}/>:'Add'}
                  </button>
                  <button className="pw-btn-cancel" style={{padding:'5px 10px',fontSize:12}} onClick={()=>setShowDatePicker(false)}>Cancel</button>
                </div>
              )}
            </div>
            {/* Prayer counts */}
            {isOwner ? (
              <>
                {totalCount > 0 && (
                  <span className="pw-pray-total">
                    🙏 {totalCount} {totalCount===1?'prayer':'prayers'}
                  </span>
                )}
                {myCount > 0 && (
                  <span className="pw-pray-total pw-pray-my">
                    {t('prayer.youPrayed', myCount)}
                  </span>
                )}
              </>
            ) : (
              myCount > 0 && (
                <span className="pw-pray-total pw-pray-my">
                  {t('prayer.youPrayed', myCount)}
                </span>
              )
            )}
          </div>
        )}
        {isAnswered && (totalCount > 0 || (isOwner && myCount > 0)) && (
          <div className="pw-note-pray-row">
            {totalCount > 0 && <span className="pw-pray-total">🙏 {totalCount} {totalCount===1?'prayer':'prayers'} total</span>}
            {isOwner && myCount > 0 && <span className="pw-pray-total pw-pray-my">{t('prayer.youPrayed', myCount)}</span>}
          </div>
        )}
        {isOwner&&!isAnswered&&(
          <div className="pw-note-actions">
            <button className="pw-note-answer-btn" onClick={()=>onMarkAnswered(item)}><PartyPopper size={13}/> {t('prayer.godAnswered')}</button>
            <div className="pw-note-action-right">
              <button className="pw-note-action-icon" onClick={()=>onEdit(item)}><Pencil size={13}/></button>
              <button className="pw-note-action-icon pw-note-delete" onClick={()=>onDelete(item)}><Trash2 size={13}/></button>
            </div>
          </div>
        )}
        {isOwner&&isAnswered&&(<div className="pw-note-actions"><div className="pw-note-action-right"><button className="pw-note-action-icon pw-note-delete" onClick={()=>onDelete(item)}><Trash2 size={13}/></button></div></div>)}
        {!isOwner&&(<div className="pw-note-actions" style={{justifyContent:'flex-end'}}><button className="pw-note-action-icon" style={{color:'var(--gray-3)'}} onClick={()=>setShowReport(true)}><Flag size={13}/></button></div>)}
        {showReport&&<ReportModal contentType="prayer_wall" contentId={item.id} onClose={()=>setShowReport(false)}/>}
      </div>
    </div>
  );
}


// ── RequestCard — compact modern card for the Requests tab list view ──────────
function RequestCard({ item, currentUserId, onEdit, onDelete, onMarkAnswered, onRemove, onUpdateAdded, onUpdateDeleted }) {
  const { t: tw } = useTranslation('prayerWall');
  const { t } = useTranslation();
  const local = useLocalizedItem(item);
  const [expanded, setExpanded]       = useState(false);
  const [menuOpen, setMenuOpen]       = useState(false);
  const [addingCal, setAddingCal]     = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickedDate, setPickedDate]   = useState('');
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [updateText, setUpdateText]   = useState('');
  const [savingUpdate, setSavingUpdate] = useState(false);
  const [showAllUpdates, setShowAllUpdates] = useState(false);
  const [calAdded, setCalAdded]       = useState(false);
  const [showReport, setShowReport]   = useState(false);
  const [showWallShare, setShowWallShare] = useState(false);
  const menuRef = useRef(null);

  const isOwner    = item.user?.id === currentUserId;
  const isAnswered = item.status === 'thankyou';
  const canShare   = item.visibility !== 'private';
  const totalCount = item.totalPrayerCount || 0;
  const myCount    = item.myPrayerCount    || 0;

  useEffect(() => {
    if (!menuOpen) return;
    function h(e) { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  async function handleAddToday() {
    setMenuOpen(false); setAddingCal(true);
    try { await prayerWallApi.addToCalendar(item.id); setCalAdded(true); }
    catch(e) { alert(e.message || 'Could not add to calendar'); }
    finally { setAddingCal(false); }
  }
  async function handleAddDate() {
    if (!pickedDate) return; setAddingCal(true);
    try { await prayerWallApi.addToCalendarDate(item.id, pickedDate); setCalAdded(true); setShowDatePicker(false); }
    catch(e) { alert(e.message || 'Could not add to calendar'); }
    finally { setAddingCal(false); }
  }
  async function handleAddUpdate() {
    if (!updateText.trim()) return;
    setSavingUpdate(true);
    try {
      const res = await prayerWallApi.addUpdate(item.id, updateText.trim());
      if (onUpdateAdded) onUpdateAdded(item.id, res.update);
      setUpdateText(''); setShowUpdateForm(false);
    } catch(e) { alert(e.message || 'Could not add update'); }
    finally { setSavingUpdate(false); }
  }

  const icon    = CATEGORY_ICONS[item.category] || '📌';
  const catIdx  = CATEGORIES_EN.indexOf(item.category);
  const catsT   = tw('categories');
  const catLabel= (Array.isArray(catsT) && catIdx >= 0) ? catsT[catIdx] : (item.category || '');

  // Visibility label
  const visBaseLabel = {
    private: tw('visPrivate'), connections: tw('visConnections'),
    groups: tw('visGroups'), 'connections+groups': `${tw('visConnections')} + ${tw('visGroups')}`,
    all: tw('visAll'),
  }[item.visibility] || tw('visPrivate');
  const visLabel = (() => {
    const hasGroups = item.visibility === 'groups' || item.visibility === 'connections+groups';
    const ids = item.targetGroupIds || [];
    if (hasGroups && ids.length > 0) {
      const g = ids.length === 1 ? '1 community' : `${ids.length} communities`;
      return item.visibility === 'connections+groups' ? `Connections + ${g}` : g;
    }
    return visBaseLabel;
  })();

  const visIcon = item.visibility === 'private' ? <Lock size={11}/> : item.visibility === 'all' ? <Globe size={11}/> : <Users size={11}/>;

  const endDatePassed = item.endDate && new Date(item.endDate) < new Date();
  const authorInitials = item.user?.name?.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase() || '?';

  return (
    <div className={`rq-card${isAnswered ? ' rq-answered' : ''}`}>
      <div className="rq-tape"/>
      <div className="rq-inner">
      {/* Header row: category chip + avatar (for others) + overflow actions */}
      <div className="rq-header">
        <span className="rq-cat-chip">
          <span className="rq-cat-icon">{icon}</span>
          <span className="rq-cat-label">{catLabel}</span>
        </span>
        {!isOwner && (
          <div className="rq-author">
            {item.user?.avatar
              ? <img src={item.user.avatar} alt={item.user?.name} className="rq-avatar-img" loading="lazy"/>
              : <div className="rq-avatar-initials">{authorInitials}</div>}
            <span className="rq-author-name">{shortName(item.user?.name)}</span>
          </div>
        )}
        {isAnswered && (
          <span className="rq-answered-badge"><CheckCircle2 size={11}/> Answered</span>
        )}
        <div className="rq-card-kebab" ref={menuRef}>
          <button className="rq-menu-btn" type="button" aria-haspopup="true" aria-expanded={menuOpen}
            onClick={() => setMenuOpen(o => !o)}>
            <Menu size={16} strokeWidth={2}/>
          </button>
          {menuOpen && (
            <div className="rq-menu" role="menu">
              {isOwner && !isAnswered && (
                <button className="rq-menu-item rq-menu-answered" role="menuitem" onClick={() => { setMenuOpen(false); onMarkAnswered(item); }}>
                  <PartyPopper size={14}/> {t('prayer.godAnswered')}
                </button>
              )}
              {!isAnswered && (
                <button className={`rq-menu-item${addingCal ? ' is-busy' : ''}`} role="menuitem" onClick={handleAddToday} disabled={addingCal}>
                  <CalendarDays size={14}/> {t('prayer.prayToday')}
                </button>
              )}
              {!isAnswered && (
                <button className="rq-menu-item" role="menuitem" onClick={() => { setShowDatePicker(true); setMenuOpen(false); }}>
                  <CalendarDays size={14}/> {t('prayer.praySpecificDay')}
                </button>
              )}
              {isOwner && !isAnswered && (
                <button className="rq-menu-item" role="menuitem" onClick={() => { setMenuOpen(false); onEdit(item); }}>
                  <Pencil size={14}/> {t('prayer.editRequest')}
                </button>
              )}
              {isOwner && (
                <button className="rq-menu-item" role="menuitem" onClick={() => { setMenuOpen(false); setShowUpdateForm(true); }}>
                  <MessageCircle size={14}/> {t('prayer.addUpdate')}
                </button>
              )}
              {canShare ? (
                <button className="rq-menu-item" role="menuitem" onClick={() => { setMenuOpen(false); setShowWallShare(true); }}>
                  <Send size={14}/> {t('feed.shareToFeed')}
                </button>
              ) : (
                <button className="rq-menu-item rq-menu-disabled" role="menuitem" disabled>
                  <Send size={14}/> {t('feed.shareToFeed')}
                  <span className="rq-menu-hint">{tw('privateLabel')}</span>
                </button>
              )}
              <div className="rq-menu-divider"/>
              {isOwner && (
                <button className="rq-menu-item rq-menu-danger" role="menuitem" onClick={() => { setMenuOpen(false); onDelete(item); }}>
                  <Trash2 size={14}/> {t('prayer.deleteRequest')}
                </button>
              )}
              {!isOwner && (
                <button className="rq-menu-item" role="menuitem" onClick={() => { setMenuOpen(false); setShowReport(true); }}>
                  <Flag size={14}/> {t('prayer.reportRequest')}
                </button>
              )}
              {!isOwner && onRemove && (
                <button className="rq-menu-item rq-menu-danger" role="menuitem" onClick={() => { setMenuOpen(false); onRemove(item); }}>
                  <X size={14}/> {t('prayer.removeFromList')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Title */}
      <p className="rq-title">{local.title}</p>

      {/* Description — collapsible */}
      {local.description && (
        <>
          <div className={`rq-desc${expanded ? ' expanded' : ''}`}>
            <p>{local.description}</p>
          </div>
          {local.description.length > 120 && (
            <button className="rq-expand-btn" onClick={() => setExpanded(v => !v)}>
              {expanded ? <><ChevronUp size={11}/> {t('prayer.showLess')}</> : <><ChevronDown size={11}/> {t('prayer.showMore')}</>}
            </button>
          )}
        </>
      )}
      <TranslationBadge isTranslated={local.isTranslated} canTranslate={local.canTranslate}
        sourceLang={local.sourceLang} onClick={local.toggle} compact/>

      {/* Updates */}
      {item.updates?.length > 0 && (
        <div className="rq-updates">
          <div className="rq-updates-label">
            <MessageCircle size={11}/> {t('prayer.updatesLabel', item.updates.length)}
          </div>
          {(showAllUpdates ? item.updates : item.updates.slice(0, 2)).map(u => (
            <LocalizedUpdateRow
              key={u.id}
              update={u}
              canDelete={isOwner}
              onDelete={() => onUpdateDeleted?.(item.id, u.id)}
            />
          ))}
          {item.updates.length > 2 && (
            <button className="rq-expand-btn" onClick={() => setShowAllUpdates(v => !v)}>
              {showAllUpdates
                ? <><ChevronUp size={11}/> {t('prayer.showLess')}</>
                : <><ChevronDown size={11}/> {t('prayer.showMoreUpdates', item.updates.length - 2)}</>}
            </button>
          )}
        </div>
      )}

      {/* Inline update form */}
      {showUpdateForm && (
        <div className="rq-update-form">
          <textarea className="rq-update-input" value={updateText} onChange={e => setUpdateText(e.target.value)}
            placeholder={t('prayer.updatePlaceholder')} maxLength={1000} rows={2} autoFocus/>
          <div className="rq-update-form-actions">
            <button className="rq-update-save" onClick={handleAddUpdate} disabled={!updateText.trim() || savingUpdate}>
              {savingUpdate ? '…' : t('common.save')}
            </button>
            <button className="rq-update-cancel" onClick={() => { setShowUpdateForm(false); setUpdateText(''); }}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Answered testimony */}
      {isAnswered && local.answeredNote && (
        <div className="rq-testimony">
          <CheckCircle2 size={12}/> <span>{local.answeredNote}</span>
        </div>
      )}

      {/* Footer row: meta left + counts/calendar right */}
      <div className="rq-footer">
        <div className="rq-meta">
          {isOwner && (
            <span className="rq-meta-chip">{visIcon} {visLabel}</span>
          )}
          {item.endDate && (
            <span className={`rq-meta-chip${endDatePassed && !isAnswered ? ' overdue' : ''}`}>
              <Clock size={11}/> {isAnswered ? tw('answeredOn') : tw('endsOn')} {fmtDateWall(isAnswered ? item.answeredAt : item.endDate)}
            </span>
          )}
          {isAnswered && item.answeredAt && !item.endDate && (
            <span className="rq-meta-chip"><CheckCircle2 size={11}/> {tw('answeredOn')} {fmtDateWall(item.answeredAt)}</span>
          )}
        </div>

        <div className="rq-counts-cal">
          {isOwner && totalCount > 0 && (
            <span className="rq-count-chip rq-count-total">🙏 {totalCount}</span>
          )}
          {myCount > 0 && (
            <span className="rq-count-chip rq-count-mine">You {myCount}×</span>
          )}
          {calAdded && (
            <span className="rq-count-chip rq-count-added"><Check size={11}/> {t('prayer.addedToCalendarShort')}</span>
          )}
        </div>
      </div>

      {showDatePicker && (
        <div className="rq-date-picker-wrap">
          <div className="pw-cal-date-picker">
            <input type="date" className="pw-input" style={{fontSize:13,padding:'5px 8px'}}
              value={pickedDate} min={new Date().toISOString().slice(0,10)} onChange={e=>setPickedDate(e.target.value)}/>
            <button className="pw-btn-save" style={{padding:'5px 12px',fontSize:12}} onClick={handleAddDate} disabled={!pickedDate||addingCal}>
              {addingCal ? <span className="spinner" style={{width:11,height:11,borderColor:'white',borderTopColor:'transparent'}}/> : 'Add'}
            </button>
            <button className="pw-btn-cancel" style={{padding:'5px 10px',fontSize:12}} onClick={() => setShowDatePicker(false)}>Cancel</button>
          </div>
        </div>
      )}
      </div>

      {showReport && <ReportModal contentType="prayer_wall" contentId={item.id} onClose={() => setShowReport(false)}/>}
      {showWallShare && canShare && (
        <ShareToFeedModal
          type={isAnswered ? 'praise' : 'prayer_request'}
          title={isAnswered ? 'Share Answered Prayer' : 'Share Prayer Request'}
          structuredMarker={`[PRAYER_REQUEST]${JSON.stringify({
            title: item.title,
            text: item.description || null,
            requesterName: isOwner ? null : item.user?.name,
            ...(isAnswered ? { isAnswered: true, answeredNote: item.answeredNote || null } : {}),
            ...(!isAnswered && item.updates?.length ? { latestUpdate: item.updates[item.updates.length - 1]?.content } : {}),
          })}[/PRAYER_REQUEST]`}
          previewLabel={isAnswered ? 'Answered Prayer' : 'Prayer Request'}
          previewContent={
            isAnswered
              ? `${item.title}${item.answeredNote ? `\n\nHow God answered: ${item.answeredNote}` : ''}`
              : `${item.title}${item.updates?.length ? `\n\nLatest update: ${item.updates[item.updates.length - 1]?.content}` : ''}`
          }
          allowedVisibilities={WALL_VIS_ALLOWED[item.visibility] || ['connections', 'friends', 'groups']}
          visibilityNote={item.visibility !== 'all'
            ? 'This request has limited visibility. You can only share it at the same or more restricted level.'
            : ''}
          onClose={() => setShowWallShare(false)}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
}

export function RequestsTab({ currentUserId, wallItems, wallLoading, onSaved, onDelete, onMarkAnswered, onUpdateAdded, onUpdateDeleted, editItem, setEditItem, answerItem, setAnswerItem }) {
  const { t } = useTranslation();
  const { t: tw } = useTranslation('prayerWall');
  const [sub, setSub] = useState('myWall'); // 'myWall' | 'prayingFor'
  const [prayingForItems, setPrayingForItems] = useState([]);
  const [pfLoading, setPfLoading] = useState(true);

  useEffect(()=>{
    prayerWallApi.prayingFor().then(d=>setPrayingForItems(d.items||[])).catch(()=>{}).finally(()=>setPfLoading(false));
  },[]);

  const myItems      = wallItems.filter(i=>i.user?.id===currentUserId);
  const pleaseItems  = myItems.filter(i=>i.status==='please');
  const thankYouItems= myItems.filter(i=>i.status==='thankyou');

  async function handleRemovePrayingFor(item){
    if(!window.confirm('Remove from your prayer list?'))return;
    await prayerWallApi.swipe(item.id,'dismissed').catch(()=>{});
    setPrayingForItems(prev=>prev.filter(i=>i.id!==item.id));
  }
  function handlePFAnswered(updated){setPrayingForItems(prev=>prev.map(i=>i.id===updated.id?updated:i));setAnswerItem(null);}

  return(
    <div>
      {/* Sub-tab bar */}
      <div className="pr-sub-tabs">
        <button className={`pr-sub-tab${sub==='myWall'?' active':''}`} onClick={()=>setSub('myWall')}>
          <BookMarked size={14}/> {t('prayer.myRequests')}
          {myItems.length>0&&<span className="pr-tab-count">{myItems.length}</span>}
        </button>
        <button className={`pr-sub-tab${sub==='prayingFor'?' active':''}`} onClick={()=>setSub('prayingFor')}>
          <HandHeart size={14}/> {t('prayer.prayingFor')}
          {prayingForItems.length>0&&<span className="pr-tab-count">{prayingForItems.length}</span>}
        </button>
      </div>

      {sub==='myWall' && (
        wallLoading
          ? <div className="pw-loading"><span className="spinner" style={{width:32,height:32}}/></div>
          : myItems.length === 0
            ? <div className="pw-discover-empty">
                <div className="pw-discover-empty-icon">🙏</div>
                <h3>No prayer requests yet</h3>
                <p>Add your first prayer request to get started.</p>
              </div>
            : <div className="rq-board">
                <div className="rq-board-col">
                  <div className="rq-board-header rq-board-please">
                    <span>🙏</span> {tw('pleaseHeader')} <span className="rq-board-count">{pleaseItems.length}</span>
                  </div>
                  <div className="rq-notes-area">
                    {pleaseItems.length === 0
                      ? <p className="rq-empty-col">{tw('emptyPlease')}</p>
                      : pleaseItems.map(item => (
                          <RequestCard key={item.id} item={item} currentUserId={currentUserId}
                            onEdit={setEditItem} onDelete={onDelete} onMarkAnswered={setAnswerItem}
                            onUpdateAdded={onUpdateAdded} onUpdateDeleted={onUpdateDeleted}/>
                        ))
                    }
                  </div>
                </div>
                <div className="rq-board-col">
                  <div className="rq-board-header rq-board-thankyou">
                    <span>✨</span> {tw('thankYouHeader')} <span className="rq-board-count">{thankYouItems.length}</span>
                  </div>
                  <div className="rq-notes-area">
                    {thankYouItems.length === 0
                      ? <p className="rq-empty-col">{tw('emptyThankYou')}</p>
                      : thankYouItems.map(item => (
                          <RequestCard key={item.id} item={item} currentUserId={currentUserId}
                            onEdit={setEditItem} onDelete={onDelete} onMarkAnswered={setAnswerItem}
                            onUpdateAdded={onUpdateAdded} onUpdateDeleted={onUpdateDeleted}/>
                        ))
                    }
                  </div>
                </div>
              </div>
      )}

      {sub==='prayingFor' && (
        pfLoading
          ? <div className="pw-loading"><span className="spinner" style={{width:32,height:32}}/></div>
          : prayingForItems.length===0
            ? <div className="pw-discover-empty">
                <div className="pw-discover-empty-icon">🙏</div>
                <h3>{t('prayer.yourPrayerListEmpty')}</h3>
                <p>{t('prayer.prayerListEmptyDesc')}</p>
              </div>
            : (() => {
                const pfPlease   = prayingForItems.filter(i => i.status === 'please');
                const pfAnswered = prayingForItems.filter(i => i.status === 'thankyou');
                return (
                  <div className="rq-pf-sections">
                    {pfPlease.length > 0 && (
                      <>
                        <div className="rq-board-header rq-board-please" style={{borderRadius:8,marginBottom:10}}>
                          <span>🙏</span> {t('prayer.activeLabel')} <span className="rq-board-count">{pfPlease.length}</span>
                        </div>
                        <div className="rq-masonry">
                          {pfPlease.map(item => (
                            <RequestCard key={item.id} item={item} currentUserId={currentUserId}
                              onEdit={setEditItem} onRemove={handleRemovePrayingFor}
                              onDelete={handleRemovePrayingFor} onMarkAnswered={setAnswerItem}
                              onUpdateAdded={onUpdateAdded} onUpdateDeleted={onUpdateDeleted}/>
                          ))}
                        </div>
                      </>
                    )}
                    {pfAnswered.length > 0 && (
                      <>
                        <div className="rq-board-header rq-board-thankyou" style={{borderRadius:8,marginBottom:10,marginTop:pfPlease.length>0?18:0}}>
                          <span>✨</span> {t('prayer.answeredLabel')} <span className="rq-board-count">{pfAnswered.length}</span>
                        </div>
                        <div className="rq-masonry">
                          {pfAnswered.map(item => (
                            <RequestCard key={item.id} item={item} currentUserId={currentUserId}
                              onEdit={setEditItem} onRemove={handleRemovePrayingFor}
                              onDelete={handleRemovePrayingFor} onMarkAnswered={setAnswerItem}
                              onUpdateAdded={onUpdateAdded} onUpdateDeleted={onUpdateDeleted}/>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()
      )}

      {editItem&&<AddEditWallModal item={editItem} onClose={()=>setEditItem(null)} onSave={(saved,isEdit)=>{onSaved(saved,isEdit);setEditItem(null);}}/>}
      {answerItem&&(sub==='prayingFor'
        ?<AnswerWallModal item={answerItem} onClose={()=>setAnswerItem(null)} onAnswered={handlePFAnswered}/>
        :<AnswerWallModal item={answerItem} onClose={()=>setAnswerItem(null)} onAnswered={(u)=>{onSaved(u,true);setAnswerItem(null);}}/>
      )}
    </div>
  );
}

function WallColumn({ label, sublabel, status, items, currentUserId, onEdit, onDelete, onMarkAnswered, headerClass }) {
  const { t: tw } = useTranslation('prayerWall');
  return(
    <div className="pw-column">
      <div className={`pw-column-header ${headerClass}`}>
        <div className="pw-col-torn-edge"/>
        <div className="pw-col-header-inner">
          <span className="pw-col-label">{label}</span>
          {sublabel&&<span className="pw-col-sublabel">{sublabel}</span>}
          <span className="pw-col-count">{items.length}</span>
        </div>
      </div>
      <div className="pw-notes-area">
        {!items.length?(
          <div className="pw-empty-col">
            {status==='please'?<><div className="pw-empty-col-icon">🙏</div><p>{tw('emptyPlease')}</p></>:<><div className="pw-empty-col-icon">✨</div><p>{tw('emptyThankYou')}</p></>}
          </div>
        ):(
          items.map(item=>(
            <StickyNote key={item.id} item={item} currentUserId={currentUserId}
              onEdit={onEdit} onDelete={onDelete} onMarkAnswered={onMarkAnswered} status={status}/>
          ))
        )}
      </div>
    </div>
  );
}

// PrayerScreen main component removed — layout + tabs now handled by
// PrayerLayout.js and individual Prayer*Screen.js files.
// Sub-components above are still exported and imported by those screens.
